import { useState, useMemo, useEffect, useRef } from "react";
import { motion, useInView, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import Lenis from "lenis";
import { IS_MOBILE } from "@/lib/isMobile";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Home, Zap, Building2, TrendingUp, ChevronDown, X, Maximize2 } from "lucide-react";
import { openCalendly } from "@/lib/calendly";
import { calculate, fmtEUR, fmtPct, KARLS_DEFAULTS, UNIT_PROFILES, SCENARIOS } from "@/lib/karlsCalc";
import DealroomLockComponent from "@/components/DealroomLock";
import DealroomShareWidget from "@/components/DealroomShareWidget";
import { webpSrc } from "@/lib/webpSrc";
import Img from "@/components/Img";
import HeroPreload from "@/components/HeroPreload";

// ─── Drone images + hero rendering (clean curated gallery)
const droneImages = [
  { src: webpSrc("/assets/karls/rendering-main.jpg"), caption: "Off Market Karlshorst — finished architectural rendering" },
  { src: "/assets/karls/aerial-construction.jpeg", caption: "Aerial view — current on-site construction status" },
  { src: webpSrc("/assets/karls/drone-1.jpg"), caption: "Off Market Karlshorst from the south-west" },
  { src: webpSrc("/assets/karls/drone-2.jpg"), caption: "Site progress over Karlshorst" },
  { src: webpSrc("/assets/karls/drone-3.jpg"), caption: "Roof line and courtyard view" },
  { src: webpSrc("/assets/karls/drone-4.jpg"), caption: "Building envelope completion" },
  { src: webpSrc("/assets/karls/drone-5.jpg"), caption: "Surrounding green Karlshorst suburb" },
  { src: webpSrc("/assets/karls/drone-6.jpg"), caption: "Connection to Regener Straße" },
];

// ─── Macro data (sourced from Karls_Living_II_Investor_Expose_EN.pdf) ───
const berlinPopulationData = [
  { year: 2014, pop: 3.47 },
  { year: 2018, pop: 3.64 },
  { year: 2022, pop: 3.82 },
  { year: 2025, pop: 3.90 },
  { year: 2030, pop: 3.94 },
  { year: 2035, pop: 3.98 },
  { year: 2040, pop: 4.01 },
];

const districtGrowthData = [
  // Berlin Senate population forecast, Nov 2025 — change to 2040
  { name: "Treptow-Köpenick", growth: 9.6, positive: true },
  { name: "Lichtenberg (Karlshorst)", growth: 8.0, positive: true, hero: true },
  { name: "Marzahn-Hellersdorf", growth: 7.0, positive: true },
  { name: "Pankow", growth: 6.6, positive: true },
  { name: "Spandau", growth: 6.1, positive: true },
  { name: "Friedrichshain-Kreuzberg", growth: 2.1, positive: false, neutral: true },
  { name: "Mitte", growth: 2.0, positive: false, neutral: true },
  { name: "Reinickendorf", growth: 1.8, positive: false, neutral: true },
  { name: "Neukölln", growth: -1.2, positive: false },
  { name: "Tempelhof-Schöneberg", growth: -1.9, positive: false },
  { name: "Charlottenburg-Wilmersdorf", growth: -2.5, positive: false },
  { name: "Steglitz-Zehlendorf", growth: -2.9, positive: false },
];

const supplyGapData = [
  { year: "2019", needed: 20000, delivered: 16800 },
  { year: "2020", needed: 20000, delivered: 15400 },
  { year: "2021", needed: 22000, delivered: 15800 },
  { year: "2022", needed: 24000, delivered: 14800 },
  { year: "2023", needed: 26000, delivered: 13900 },
  { year: "2024", needed: 28000, delivered: 11200 },
];

const unitMixData = [
  { name: "2-room (high demand)", value: 73, color: "#D4AF6A" },
  { name: "3-room (family)", value: 26, color: "#A98850" },
  { name: "4-room penthouse", value: 1, color: "#6B5430" },
];

// Energy consumption — dena Gebäudereport + preliminary energy certificate
const energyData = [
  { era: "Pre-1945", kwh: 144.8, isKarls: false },
  { era: "1946–1976", kwh: 148.0, isKarls: false },
  { era: "1977–1983", kwh: 142.2, isKarls: false },
  { era: "1984–1994", kwh: 127.8, isKarls: false },
  { era: "1995–2001", kwh: 99.1, isKarls: false },
  { era: "2002–2007", kwh: 81.8, isKarls: false },
  { era: "2007+", kwh: 73.2, isKarls: false },
  { era: "Off Market Karlshorst", kwh: 11.8, isKarls: true },
];

// ─── Chart Theme ───
const chartTheme = {
  axis: "rgba(255,255,255,0.4)",
  grid: "rgba(255,255,255,0.06)",
  accent: "#D4AF6A", // gold = positive / growing / good (all growth districts)
  accentSoft: "#B89255", // dark gold = positive (deepest)
  negative: "#8B4A4A", // muted brick red = shrinking / risk
  neutral: "rgba(255,255,255,0.18)", // monochrome muted (replaces blue/slate) for context bars
  karlsHero: "#F5D58A", // luminous bright gold = Off Market Karlshorst / Karlshorst hero highlight (with glow filter)
  karlsHeroStroke: "#FFE9B0", // bright outer stroke for the hero bar
  ink: "#ffffff",
};

const ChartTooltip = ({ active, payload, label, suffix = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a0a0a]/95 backdrop-blur-md border border-ire-accent/20 px-4 py-3 text-xs">
      {label !== undefined && <div className="font-heading text-white/60 mb-1.5">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: p.color || p.fill }} />
          <span className="text-white/70">{p.name}:</span>
          <span className="text-ire-accent font-mono tabular-nums">
            {typeof p.value === "number" ? p.value.toLocaleString("de-DE", { maximumFractionDigits: 2 }) : p.value}
            {suffix}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Reusable: Apple-style Reveal on scroll (scale + blur ease) ───
const Reveal = ({ children, delay = 0, className = "", y = 36, scale = 0.97, blur = 8 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y, scale, filter: `blur(${blur}px)` }}
      animate={inView ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" } : {}}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ─── Apple-style scroll progress HUD (thin gold rule at top) ───
const ScrollProgressHUD = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  return (
    <>
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] z-[60] origin-left pointer-events-none"
        style={{
          scaleX,
          background: "linear-gradient(90deg, transparent, #F5D58A 30%, #FFE9B0 50%, #F5D58A 70%, transparent)",
          boxShadow: "0 0 14px rgba(255,233,176,0.5), 0 0 6px rgba(255,233,176,0.8)",
        }}
      />
    </>
  );
};

// ─── Iron Man-style ambient gold cursor glow (subtle, follows pointer) ───
const AmbientCursorGlow = () => {
  const mouseX = useMotionValue(-200);
  const mouseY = useMotionValue(-200);
  const x = useSpring(mouseX, { stiffness: 80, damping: 22 });
  const y = useSpring(mouseY, { stiffness: 80, damping: 22 });
  useEffect(() => {
    const onMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [mouseX, mouseY]);
  return (
    <motion.div
      aria-hidden
      className="fixed top-0 left-0 w-[520px] h-[520px] rounded-full pointer-events-none z-[5] mix-blend-screen opacity-50 hidden md:block"
      style={{
        x,
        y,
        translateX: "-50%",
        translateY: "-50%",
        background: "radial-gradient(circle, rgba(245,213,138,0.10) 0%, rgba(245,213,138,0.03) 35%, transparent 70%)",
      }}
    />
  );
};

// ─── Apple-style section anchor mini-nav (floats right edge) ───
const ANCHOR_SECTIONS = [
  { id: "section-macro", label: "Macro" },
  { id: "section-asset", label: "Asset" },
  { id: "section-faq", label: "FAQ" },
];

const SectionAnchorNav = () => {
  const [activeId, setActiveId] = useState("section-macro");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only after the user has scrolled past the hero
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    // Track active section via IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the centre of the viewport
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length === 0) return;
        // Choose the one whose centre is closest to viewport centre
        const vpCenter = window.innerHeight / 2;
        const best = visibleEntries.reduce((best, e) => {
          const r = e.boundingClientRect;
          const center = r.top + r.height / 2;
          const dist = Math.abs(center - vpCenter);
          return !best || dist < best.dist ? { id: e.target.id, dist } : best;
        }, null);
        if (best) setActiveId(best.id);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: "-20% 0px -20% 0px" }
    );
    ANCHOR_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const lenis = window.__lenis;
    if (lenis) {
      lenis.scrollTo(el, { offset: -80, duration: 1.6 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: visible ? 1 : 0, x: visible ? 0 : 24 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-1/2 -translate-y-1/2 right-6 md:right-8 z-50 pointer-events-none hidden md:flex flex-col items-end gap-5"
      data-testid="section-anchor-nav"
      aria-hidden={!visible}
    >
      {ANCHOR_SECTIONS.map((s) => {
        const active = activeId === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollTo(s.id)}
            data-testid={`anchor-${s.id}`}
            className="group relative flex items-center gap-3 pointer-events-auto py-1.5 pl-3 -mr-1"
            aria-label={`Jump to ${s.label}`}
            aria-current={active ? "true" : undefined}
          >
            {/* Label — only visible on hover */}
            <span
              className={`font-body text-[10px] tracking-[0.35em] uppercase transition-all duration-500 ${
                active ? "text-ire-accent opacity-100" : "text-white/55 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0"
              }`}
            >
              {s.label}
            </span>
            {/* Dot */}
            <span className="relative inline-flex">
              <motion.span
                animate={active ? { scale: 1.0 } : { scale: 0.7 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className={`block w-2.5 h-2.5 rounded-full ${active ? "" : "bg-white/30 group-hover:bg-white/60"}`}
                style={
                  active
                    ? {
                        background: "radial-gradient(circle, #FFE9B0 0%, #F5D58A 60%, #D4AF6A 100%)",
                        boxShadow:
                          "0 0 14px rgba(255,233,176,0.85), 0 0 4px rgba(255,233,176,1), 0 0 28px rgba(245,213,138,0.45)",
                      }
                    : undefined
                }
              />
              {/* Active outer ring pulse */}
              {active && (
                <motion.span
                  initial={{ opacity: 0.6, scale: 1 }}
                  animate={{ opacity: 0, scale: 2 }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                  className="absolute inset-0 rounded-full border border-ire-accent"
                />
              )}
            </span>
          </button>
        );
      })}
    </motion.div>
  );
};

// ═════════════════════════════════════════════════════════
// ACT 1 — Macro Case
// ═════════════════════════════════════════════════════════
const MacroCase = () => {
  return (
    <section className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04]">
      <div className="max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">Act I</span>
            <div className="h-px w-12 bg-ire-accent/40" />
            <span className="font-body text-[11px] tracking-[0.4em] text-white/40 uppercase">The Macro Case</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-8 max-w-4xl">
            Demand outpacing supply.<br />
            <span className="text-ire-accent italic">Lichtenberg leads growth.</span>
          </h2>
          <p className="font-body text-base sm:text-lg text-white/60 max-w-3xl leading-relaxed mb-20">
            Berlin is projected to gain 109,000 additional residents by 2040 — a +2.8% growth on top of an already
            tight market. Karlshorst sits in Lichtenberg, the city's second-fastest-growing district, where
            structurally low vacancy and a widening delivery gap make new-build rental supply scarce and durable.
          </p>
        </Reveal>

        {/* ─── Chart Grid ─── */}
        <div className="grid lg:grid-cols-5 gap-6 md:gap-8">
          {/* Chart 1: Population trend */}
          <Reveal delay={0.1} className="lg:col-span-3 bg-[#0a0a0a] border border-white/[0.06] p-6 md:p-8">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
              <div>
                <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Chart 01</div>
                <h3 className="font-heading text-xl md:text-2xl text-white">Berlin population — forecast to 2040</h3>
              </div>
              <div className="font-mono text-xs text-white/40">in millions</div>
            </div>
            <div className="h-[280px] md:h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={berlinPopulationData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="year" stroke={chartTheme.axis} tick={{ fontSize: 11, fill: chartTheme.axis }} tickLine={false} axisLine={{ stroke: chartTheme.grid }} />
                  <YAxis stroke={chartTheme.axis} tick={{ fontSize: 11, fill: chartTheme.axis }} tickLine={false} axisLine={false} domain={[3.4, 4.05]} />
                  <Tooltip content={<ChartTooltip suffix=" M" />} cursor={{ stroke: chartTheme.accent, strokeOpacity: 0.3 }} />
                  <Line
                    type="monotone"
                    dataKey="pop"
                    name="Population"
                    stroke={chartTheme.accent}
                    strokeWidth={2}
                    dot={{ r: 4, fill: chartTheme.accent, stroke: chartTheme.accent }}
                    activeDot={{ r: 6, fill: chartTheme.accent }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-white/[0.06]">
              <div>
                <div className="font-mono text-2xl text-ire-accent tabular-nums">+109k</div>
                <div className="font-body text-[10px] tracking-[0.2em] text-white/40 uppercase mt-1">By 2040</div>
              </div>
              <div>
                <div className="font-mono text-2xl text-white tabular-nums">+2.8%</div>
                <div className="font-body text-[10px] tracking-[0.2em] text-white/40 uppercase mt-1">Growth</div>
              </div>
              <div>
                <div className="font-mono text-2xl text-white tabular-nums">4.01M</div>
                <div className="font-body text-[10px] tracking-[0.2em] text-white/40 uppercase mt-1">2040 Forecast</div>
              </div>
            </div>
          </Reveal>

          {/* Chart 4: Unit mix donut */}
          <Reveal delay={0.2} className="lg:col-span-2 bg-[#0a0a0a] border border-white/[0.06] p-6 md:p-8">
            <div className="mb-6">
              <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Chart 02</div>
              <h3 className="font-heading text-xl md:text-2xl text-white">Unit mix — weighted to demand</h3>
            </div>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={unitMixData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {unitMixData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip suffix=" units" />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-4 pt-4 border-t border-white/[0.06]">
              {unitMixData.map((u) => (
                <div key={u.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2" style={{ background: u.color }} />
                    <span className="text-white/70">{u.name}</span>
                  </div>
                  <span className="font-mono text-ire-accent tabular-nums">{u.value}</span>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Chart 03: District growth — all 12 boroughs · FULL WIDTH for editorial premium feel */}
          <Reveal delay={0.15} className="lg:col-span-5 bg-[#0a0a0a] border border-white/[0.06] p-6 md:p-10">
            <div className="flex items-baseline justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Chart 03</div>
                <h3 className="font-heading text-2xl md:text-3xl text-white">District growth — population change to 2040</h3>
                <div className="font-mono text-xs text-white/40 mt-1.5">All 12 Berlin boroughs · %</div>
              </div>
              <div className="font-mono text-[11px] text-white/45 max-w-[320px] text-right leading-relaxed">
                <span style={{ color: chartTheme.karlsHeroStroke }}>Gold-glow</span> = Off Market Karlshorst location · <span style={{ color: chartTheme.negative }}>Red</span> = shrinking by 2040.
              </div>
            </div>
            <div className="h-[420px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={districtGrowthData} layout="vertical" margin={{ top: 5, right: 40, left: 0, bottom: 0 }}>
                  <defs>
                    {/* Gold glow filter for the hero bar */}
                    <filter id="karlsGoldGlow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                    <linearGradient id="karlsGoldGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#F5D58A" />
                      <stop offset="50%" stopColor="#FFE9B0" />
                      <stop offset="100%" stopColor="#F5D58A" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={chartTheme.grid} horizontal={false} />
                  <XAxis type="number" stroke={chartTheme.axis} tick={{ fontSize: 12, fill: chartTheme.axis }} tickLine={false} axisLine={false} />
                  <YAxis dataKey="name" type="category" stroke={chartTheme.axis} tick={{ fontSize: 12, fill: chartTheme.axis }} tickLine={false} axisLine={false} width={230} />
                  <Tooltip content={<ChartTooltip suffix=" %" />} cursor={{ fill: "rgba(245,213,138,0.05)" }} />
                  <Bar dataKey="growth" name="Growth to 2040" radius={[0, 2, 2, 0]}>
                    {districtGrowthData.map((d, i) => {
                      if (d.hero) {
                        return (
                          <Cell
                            key={i}
                            fill="url(#karlsGoldGradient)"
                            stroke={chartTheme.karlsHeroStroke}
                            strokeWidth={1.5}
                            style={{ filter: "url(#karlsGoldGlow)" }}
                          />
                        );
                      }
                      const color = d.growth < 0 ? chartTheme.negative : chartTheme.accentSoft;
                      return <Cell key={i} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 pt-6 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-3 text-[11px]">
              <div className="flex items-center gap-2.5" style={{ color: chartTheme.karlsHeroStroke }}>
                <span
                  className="inline-block w-2.5 h-2.5 rounded-[1px]"
                  style={{
                    background: "linear-gradient(90deg, #F5D58A, #FFE9B0, #F5D58A)",
                    boxShadow: "0 0 12px rgba(255,233,176,0.6), 0 0 4px rgba(255,233,176,0.9)",
                  }}
                />
                <span>Off Market Karlshorst is in Lichtenberg · Karlshorst</span>
              </div>
              <span className="text-white/30 font-mono">Source: Berlin Senate population forecast, Nov 2025</span>
            </div>
          </Reveal>

          {/* Chart 04: Supply gap — given equal width alongside Energy */}
          <Reveal delay={0.2} className="lg:col-span-3 bg-[#0a0a0a] border border-white/[0.06] p-6 md:p-8">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
              <div>
                <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Chart 04</div>
                <h3 className="font-heading text-xl md:text-2xl text-white">Supply gap — units needed vs delivered</h3>
              </div>
              <div className="font-mono text-xs text-white/40">Berlin, per year</div>
            </div>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={supplyGapData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="year" stroke={chartTheme.axis} tick={{ fontSize: 11, fill: chartTheme.axis }} tickLine={false} axisLine={{ stroke: chartTheme.grid }} />
                  <YAxis stroke={chartTheme.axis} tick={{ fontSize: 11, fill: chartTheme.axis }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip suffix=" units" />} cursor={{ fill: "rgba(212,175,106,0.05)" }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.axis }} iconType="square" iconSize={10} />
                  <Bar dataKey="needed" name="Annual demand" fill={chartTheme.neutral} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="delivered" name="Actually delivered" fill={chartTheme.accent} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 pt-6 border-t border-white/[0.06] text-xs text-white/55 leading-relaxed">
              Annual demand has accelerated while delivery has collapsed — the 2024 deficit alone is ~16,800 units.
              This structural undersupply is the foundation of long-duration rental durability in Berlin.
            </div>
          </Reveal>

          {/* Chart 05: Energy consumption — balanced alongside Supply gap */}
          <Reveal delay={0.25} className="lg:col-span-2 bg-[#0a0a0a] border border-white/[0.06] p-6 md:p-8">
            <div className="mb-6">
              <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Chart 05</div>
              <h3 className="font-heading text-xl md:text-2xl text-white">Energy — Off Market Karlshorst vs Berlin stock</h3>
              <div className="font-mono text-xs text-white/40 mt-1">kWh/m²/yr · by construction era</div>
            </div>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={energyData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <filter id="karlsGoldGlowSmall" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="5" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                    <linearGradient id="karlsGoldGradientV" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FFE9B0" />
                      <stop offset="100%" stopColor="#F5D58A" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="era" stroke={chartTheme.axis} tick={{ fontSize: 9, fill: chartTheme.axis }} tickLine={false} axisLine={{ stroke: chartTheme.grid }} interval={0} angle={-30} textAnchor="end" height={60} />
                  <YAxis stroke={chartTheme.axis} tick={{ fontSize: 10, fill: chartTheme.axis }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip suffix=" kWh/m²" />} cursor={{ fill: "rgba(245,213,138,0.05)" }} />
                  <Bar dataKey="kwh" name="Heating demand" radius={[2, 2, 0, 0]}>
                    {energyData.map((e, i) => {
                      if (e.isKarls) {
                        return (
                          <Cell
                            key={i}
                            fill="url(#karlsGoldGradientV)"
                            stroke={chartTheme.karlsHeroStroke}
                            strokeWidth={1.5}
                            style={{ filter: "url(#karlsGoldGlowSmall)" }}
                          />
                        );
                      }
                      return <Cell key={i} fill={chartTheme.accentSoft} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-5 pt-5 border-t border-white/[0.06] flex items-center gap-2.5 text-[11px]" style={{ color: chartTheme.karlsHeroStroke }}>
              <span
                className="inline-block w-2.5 h-2.5 rounded-[1px]"
                style={{
                  background: "linear-gradient(180deg, #FFE9B0, #F5D58A)",
                  boxShadow: "0 0 10px rgba(255,233,176,0.55)",
                }}
              />
              <span>11.8 kWh/m²/yr — KfW 40 NH · 92% less than pre-1945</span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// ACT 2 — Asset Details
// ═════════════════════════════════════════════════════════
const specs = [
  { label: "Total units", value: "100", sub: "73 × 2-room, 26 × 3-room, 1 penthouse" },
  { label: "Total living area", value: "5,800.63 m²", sub: "Avg. 57.94 m² / unit" },
  { label: "Average price", value: "€7,520/m²", sub: "Range €7,520 – €7,922 / m²" },
  { label: "Entry price", value: "from €328,525", sub: "Smallest 2-room WE" },
  { label: "Target rent", value: "€16 – €19 / m²", sub: "Cold rent, premium upper range" },
  { label: "Energy class", value: "KfW 40 NH", sub: "QNG Plus certified" },
  { label: "Completion", value: "Q4 2028", sub: "On schedule" },
  { label: "Parking", value: "€34,000", sub: "Per stellplatz, optional" },
];

const usps = [
  { icon: TrendingUp, title: "Berlin's #2 growth district", body: "Lichtenberg leads 10-year population growth with +12.4%, well ahead of all inner-ring boroughs." },
  { icon: Zap, title: "KfW 40 NH + QNG Plus", body: "Qualifies for preferential KfW financing and 5% degressive AfA plus §7b Sonder-AfA (4 × 5%)." },
  { icon: Building2, title: "Modern, barrier-reduced layouts", body: "Open-plan living, walk-in rainshowers, private balconies — designed to attract and retain quality tenants." },
  { icon: MapPin, title: "Strategic Karlshorst location", body: "Tram in 5 min, S-Bahn S3, 2 universities, 26,500 students, leafy historic garden suburb." },
];

const Gallery = ({ images }) => {
  const [active, setActive] = useState(0);
  const main = images[active];
  return (
    <div>
      <div className="relative aspect-[16/10] overflow-hidden bg-[#0a0a0a] border border-white/[0.06]">
        <motion.img
          key={main.src}
          src={main.src}
          alt={main.caption}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 w-full h-full object-cover object-[center_35%]"
          loading="lazy"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-6 md:p-8">
          <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-1">
            Frame {String(active + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </div>
          <div className="font-heading text-base md:text-lg text-white">{main.caption}</div>
        </div>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mt-3">
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            data-testid={`gallery-thumb-${i}`}
            className={`relative aspect-[4/3] overflow-hidden border transition-all duration-300 ${
              i === active ? "border-ire-accent" : "border-white/[0.06] opacity-50 hover:opacity-90"
            }`}
          >
            <img src={img.src} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Reusable zoomable image card (lightbox)
const ZoomableImageCard = ({ src, alt, kicker, title, testId, fit = "contain", position = "center" }) => {
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => { if (e.key === "Escape") setZoom(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [zoom]);
  return (
    <>
      <div
        className="relative bg-[#0a0a0a] border border-white/[0.08] aspect-[16/10] overflow-hidden group cursor-zoom-in"
        data-testid={testId}
        onClick={() => setZoom(true)}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          style={{ objectPosition: position }}
          className={`absolute inset-0 w-full h-full object-${fit} transition-transform duration-700 group-hover:scale-[1.02]`}
        />
        <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/80 backdrop-blur px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <Maximize2 className="w-3.5 h-3.5 text-ire-accent" />
          <span className="font-body text-[10px] tracking-[0.2em] uppercase text-white">Click to zoom</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-5 md:p-6 pt-12">
          <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-1">{kicker}</div>
          <div className="font-heading text-base md:text-lg text-white">{title}</div>
        </div>
      </div>
      {zoom && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={() => setZoom(false)}
          data-testid={`${testId}-zoom`}
        >
          <button
            onClick={() => setZoom(false)}
            data-testid={`${testId}-close`}
            className="absolute top-5 right-5 flex items-center gap-2 bg-white/10 hover:bg-ire-accent hover:text-black backdrop-blur px-4 py-2.5 text-white transition-colors z-10"
          >
            <X className="w-4 h-4" />
            <span className="font-body text-[10px] tracking-[0.25em] uppercase">Close</span>
          </button>
          <div className="absolute top-5 left-5 max-w-md z-10">
            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-1">{kicker}</div>
            <div className="font-body text-xs text-white/80">{title}</div>
          </div>
          <motion.img
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            src={src}
            alt={alt}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </>
  );
};

// ─── Floor plan viewer with click-to-zoom modal
const floorPlans = [
  {
    id: "unit",
    label: "Sample unit · WE 3.2.08",
    sub: "2-room · 63 m² · WA 2b",
    description: "Detailed apartment layout — living area open to kitchen, separate bedroom with built-in wardrobe, walk-in rainshower bathroom, private loggia.",
    src: webpSrc("/assets/karls/floorplan-unit-we3208.jpg"),
  },
  {
    id: "overview",
    label: "Etagenplan · architect's master plan",
    sub: "Full floor overview · all units",
    description: "The official architectural Etagenplan from the developer document — showing all units on a typical floor with circulation, common areas, balcony orientations, and unit numbering.",
    src: webpSrc("/assets/karls/floorplan-overview.jpg"),
  },
  {
    id: "hp",
    label: "Erdgeschoss · WA 2b",
    sub: "Ground-floor layout · 6 apartments",
    description: "Full floor plan showing 6 apartments on the ground floor — barrier-reduced access, common-area entrance, bike storage, mailbox area.",
    src: webpSrc("/assets/karls/floorplan-floor-hp.jpg"),
  },
  {
    id: "dg",
    label: "Dachgeschoss · WA 2b",
    sub: "Top-floor penthouse layout",
    description: "Top-floor configuration with penthouse-style units, generous private rooftop terraces, and elevated views over Karlshorst.",
    src: webpSrc("/assets/karls/floorplan-floor-dg.jpg"),
  },
];

const FloorPlansSection = () => {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const plan = floorPlans[active];

  // Close on Escape
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [zoom]);

  return (
    <>
      <Reveal delay={0.1}>
        <div className="mb-24" data-testid="floor-plans-section">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
            <div>
              <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Floor plans & layouts</div>
              <h3 className="font-heading text-2xl md:text-3xl text-white">See the architecture</h3>
              <div className="font-body text-sm text-white/70 mt-1.5 max-w-2xl">
                Single-unit layout and full floor configurations — direct from the developer's CAD drawings. Click any plan to zoom.
              </div>
            </div>
            <div className="font-mono text-[11px] text-white/45 max-w-[280px] text-right">
              Plans are illustrative — exact dimensions per individual unit contract
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Plan selector tabs */}
            <div className="lg:col-span-3 space-y-3">
              {floorPlans.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActive(i)}
                  data-testid={`floorplan-tab-${p.id}`}
                  className={`w-full text-left p-5 border transition-all duration-300 ${
                    i === active
                      ? "bg-ire-accent/[0.06] border-ire-accent/40 shadow-[0_0_40px_-15px_rgba(212,175,106,0.4)]"
                      : "bg-[#050505] border-white/[0.06] hover:border-white/15"
                  }`}
                >
                  <div className={`font-body text-[10px] tracking-[0.25em] uppercase mb-2 ${i === active ? "text-ire-accent" : "text-white/55"}`}>
                    {String(i + 1).padStart(2, "0")} / {String(floorPlans.length).padStart(2, "0")}
                  </div>
                  <div className="font-heading text-base text-white mb-1">{p.label}</div>
                  <div className="font-body text-xs text-white/65">{p.sub}</div>
                </button>
              ))}
            </div>

            {/* Main plan viewer */}
            <div className="lg:col-span-9">
              <div
                className="relative bg-white border border-white/[0.08] aspect-[16/11] overflow-hidden group cursor-zoom-in"
                onClick={() => setZoom(true)}
                data-testid="floorplan-viewer"
              >
                <motion.img
                  key={plan.src}
                  src={plan.src}
                  alt={plan.label}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-contain p-6"
                  loading="lazy"
                />
                {/* Zoom hint */}
                <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/80 backdrop-blur px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="w-3.5 h-3.5 text-ire-accent" />
                  <span className="font-body text-[10px] tracking-[0.2em] uppercase text-white">Click to zoom</span>
                </div>
              </div>
              <div className="bg-[#050505] border border-white/[0.06] border-t-0 p-5 md:p-6">
                <div className="font-heading text-base md:text-lg text-white mb-2">{plan.label}</div>
                <p className="font-body text-sm text-white/70 leading-relaxed">{plan.description}</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Zoom modal */}
      {zoom && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={() => setZoom(false)}
        >
          <button
            onClick={() => setZoom(false)}
            data-testid="floorplan-close"
            className="absolute top-5 right-5 flex items-center gap-2 bg-white/10 hover:bg-ire-accent hover:text-black backdrop-blur px-4 py-2.5 text-white transition-colors"
          >
            <X className="w-4 h-4" />
            <span className="font-body text-[10px] tracking-[0.25em] uppercase">Close</span>
          </button>
          <div className="absolute top-5 left-5 max-w-md">
            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-1">{plan.label}</div>
            <div className="font-body text-xs text-white/65">{plan.sub}</div>
          </div>
          <motion.img
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            src={plan.src}
            alt={plan.label}
            className="max-w-full max-h-full bg-white object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </>
  );
};

const AssetDetails = () => {
  return (
    <section className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04] bg-[#0a0a0a]/40">
      <div className="max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">Act II</span>
            <div className="h-px w-12 bg-ire-accent/40" />
            <span className="font-body text-[11px] tracking-[0.4em] text-white/40 uppercase">The Asset</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-8 max-w-4xl">
            Off Market Karlshorst<br />
            <span className="text-ire-accent italic">100 future-proof apartments.</span>
          </h2>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/50 mb-16">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-ire-accent/70" /> Regener Straße 64–74, 10318 Berlin</span>
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-ire-accent/70" /> Completion Q4 2028</span>
            <span className="flex items-center gap-2"><Home className="w-4 h-4 text-ire-accent/70" /> G+P Gartenstadt Karlshorst GmbH</span>
          </div>
        </Reveal>

        {/* ─── Gallery + Spec Grid ─── */}
        <div className="grid lg:grid-cols-12 gap-8 md:gap-12 mb-24">
          <Reveal delay={0.1} className="lg:col-span-7">
            <Gallery images={droneImages} />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 flex flex-col gap-px bg-white/[0.06]">
            <div className="grid grid-cols-2 gap-px bg-white/[0.06] flex-1">
              {specs.map((s) => (
                <div key={s.label} className="bg-[#050505] p-5 md:p-6 flex flex-col justify-center">
                  <div className="font-body text-[10px] tracking-[0.25em] text-white/40 uppercase mb-2">{s.label}</div>
                  <div className="font-heading text-xl md:text-2xl text-ire-accent tabular-nums mb-1">{s.value}</div>
                  <div className="font-body text-[11px] text-white/40 leading-snug">{s.sub}</div>
                </div>
              ))}
            </div>
            {/* Investment-thesis strip fills any remaining height alongside gallery */}
            <div className="bg-[#050505] p-5 md:p-6 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-ire-accent/[0.06] rounded-full blur-3xl pointer-events-none" />
              <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">The thesis · 30 sec</div>
              <p className="font-body text-sm text-white/70 leading-relaxed">
                KfW 40 NH new-build in Berlin's #2 growth district. Priced today, delivered Q4 2028. Full degressive AfA + §7b Sonder-AfA stack — your high tax bracket subsidises the equity build.
              </p>
            </div>
          </Reveal>
        </div>

        {/* ─── Interior Renderings ─── */}
        <Reveal delay={0.1}>
          <div className="mb-24">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
              <div>
                <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Renderings</div>
                <h3 className="font-heading text-2xl md:text-3xl text-white">Finished interiors — specification standard</h3>
              </div>
              <div className="font-mono text-[11px] text-white/40 max-w-[280px] text-right">
                Interior renderings — actual finishes subject to minor variation
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-px bg-white/[0.06]">
              {[
                { src: webpSrc("/assets/karls/interior-living-room.jpg"), label: "Living / dining", note: "Oak parquet, balcony view, integrated TV wall" },
                { src: webpSrc("/assets/karls/interior-kitchen.jpg"), label: "Open-plan kitchen", note: "Handleless cabinetry, integrated appliances" },
                { src: webpSrc("/assets/karls/interior-bathroom.jpg"), label: "Dining + open kitchen", note: "Statement lighting, balcony access" },
                { src: webpSrc("/assets/karls/interior-bedroom.jpg"), label: "Bedroom", note: "Natural light, generous proportions" },
              ].map((r, i) => (
                <div key={i} className="bg-[#050505] group overflow-hidden">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={r.src}
                      alt={r.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5 md:p-6 flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-heading text-base md:text-lg text-white">{r.label}</div>
                    <div className="font-body text-xs text-white/40">{r.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* ─── Location & Neighbourhood ─── */}
        <Reveal delay={0.1}>
          <div className="mb-24" data-testid="location-section">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-8">
              <div>
                <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Location & neighbourhood</div>
                <h3 className="font-heading text-2xl md:text-3xl text-white">Where it sits in Berlin</h3>
                <div className="font-body text-sm text-white/70 mt-1.5 max-w-2xl">
                  Karlshorst — a leafy, established south-eastern district with two universities, the Tierpark, and a 25-minute S-Bahn ride to Mitte.
                </div>
              </div>
              <div className="font-mono text-[11px] text-white/40 max-w-[240px] text-right hidden md:block">
                Click any map to enlarge · annotated by developer
              </div>
            </div>

            {/* Premium editorial location frame */}
            <div className="relative">
              {/* ambient glow behind */}
              <div className="absolute -inset-x-10 -inset-y-6 bg-ire-accent/[0.04] blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-2 gap-px bg-white/[0.08] border border-ire-accent/15 shadow-[0_0_60px_-20px_rgba(212,175,106,0.25)]">
                {/* Macro Berlin position */}
                <div className="bg-[#050505]">
                  <ZoomableImageCard
                    src={webpSrc("/assets/karls/location-map.jpg")}
                    alt="Off Market Karlshorst location within Berlin"
                    kicker="Macro position · 25 min to Mitte"
                    title="Off Market Karlshorst → Berlin Mitte"
                    testId="location-card-macro"
                    fit="cover"
                  />
                </div>

                {/* Surroundings POI map — contain so the legend + map sit cleanly inside the frame */}
                <div className="bg-[#050505]">
                  <ZoomableImageCard
                    src={webpSrc("/assets/karls/site-poi.jpg")}
                    alt="Off Market Karlshorst neighbourhood amenities"
                    kicker="Walking distance · 32 amenities"
                    title="Karlshorst surroundings"
                    testId="location-card-poi"
                    fit="contain"
                  />
                </div>
              </div>

              {/* Premium connection facts — integrated under the maps with thin gold rule */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/[0.08] border border-white/[0.06]">
                {[
                  { label: "S-Bahn Karlshorst", value: "5 min", sub: "Walk · 360 m" },
                  { label: "Tram", value: "5 min", sub: "M17 · 27 · 37" },
                  { label: "HTW + HWR campus", value: "<10 min", sub: "26,500 students" },
                  { label: "Tierpark", value: "12 min", sub: "Berlin's largest zoo" },
                  { label: "Mitte / Hauptbahnhof", value: "25 min", sub: "S-Bahn S3 direct" },
                ].map((f) => (
                  <div key={f.label} className="bg-[#050505] p-5 md:p-6 relative group">
                    <div className="absolute top-0 left-0 h-px w-8 bg-ire-accent/60 opacity-60 group-hover:w-full group-hover:opacity-100 transition-all duration-700" />
                    <div className="font-body text-[10px] tracking-[0.3em] text-white/55 uppercase mb-3">{f.label}</div>
                    <div className="font-heading text-2xl md:text-3xl text-ire-accent tabular-nums leading-none">{f.value}</div>
                    <div className="font-body text-[11px] text-white/50 mt-2">{f.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* ─── USPs ─── */}
        <Reveal delay={0.1}>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06]">
            {usps.map((u, i) => {
              const Icon = u.icon;
              return (
                <div key={i} className="bg-[#050505] p-7 md:p-8 group hover:bg-[#0a0a0a] transition-colors duration-500">
                  <Icon className="w-6 h-6 text-ire-accent mb-5 group-hover:scale-110 transition-transform duration-500" />
                  <h3 className="font-heading text-lg text-white mb-3 leading-snug">{u.title}</h3>
                  <p className="font-body text-sm text-white/55 leading-relaxed">{u.body}</p>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// ACT II.V — FAQ / Objection Handler
// ═════════════════════════════════════════════════════════
const faqItems = [
  {
    q: "Why buy now if it's not finished until late 2028?",
    a: "The longer construction timeline is already reflected in the purchase price — this property is priced below what comparable finished quality would cost today. And you don't pay the full purchase price immediately. Payments follow construction progress under MABV. By the time notary appointment, land register processing and ownership transfer are complete, several months have passed regardless. Interest only accrues on amounts actually disbursed — not on the full purchase price from day one.",
  },
  {
    q: "What about construction-period interest?",
    a: "Construction-period interest is real and we won't pretend otherwise. But it applies only to disbursed amounts — not the full purchase price. For investors with rental intent, these costs are tax-relevant as Werbungskosten via Anlage V. The key is documenting rental intention in the purchase contract from day one, which we handle as standard. Your Steuerberater can then work with this directly.",
  },
  {
    q: "What if the developer runs into problems?",
    a: "Grimm Holding is not dependent on individual unit sales to continue building. The developer holds sufficient equity to complete the project regardless of how many units are sold. Unsold units go into Grimm's own rental portfolio — this is standard practice across their 35-year track record. You are not buying into a project that only continues if enough buyers pay in.",
  },
  {
    q: "What if I wait six months and buy when it's closer to completion?",
    a: "You can. But today you choose your floor, your orientation, your layout, your position within the building. In six months you choose from what remains. Over a 10-year hold, the right unit — better floor, better aspect, better rentability — matters far more than saving a small amount of construction-period interest. If you wait over €2,000–3,000 in interest savings, you may end up with a significantly less rentable unit.",
  },
];

const FaqAccordionItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.06] last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group"
      >
        <div className="flex items-start gap-5">
          <span className="font-mono text-xs text-ire-accent/60 tabular-nums shrink-0 pt-1">{String(idx + 1).padStart(2, "0")}</span>
          <span className="font-heading text-lg md:text-xl text-white group-hover:text-ire-accent transition-colors duration-300">{q}</span>
        </div>
        <ChevronDown className={`w-5 h-5 text-white/40 shrink-0 mt-1 transition-transform duration-500 ${open ? "rotate-180 text-ire-accent" : ""}`} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <p className="font-body text-sm md:text-base text-white/60 leading-relaxed pl-11 pr-6 pb-7 md:pb-8 max-w-3xl">{a}</p>
      </motion.div>
    </div>
  );
};

const FaqObjections = () => {
  return (
    <section className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04]">
      <div className="max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">Act II.V</span>
            <div className="h-px w-12 bg-ire-accent/40" />
            <span className="font-body text-[11px] tracking-[0.4em] text-white/40 uppercase">Objections, answered</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-5 max-w-4xl">
            The questions every<br />
            <span className="text-ire-accent italic">investor asks.</span>
          </h2>
          <p className="font-body text-base sm:text-lg text-white/60 max-w-3xl leading-relaxed mb-14">
            We answer them here, not after you've already decided.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-[#0a0a0a] border border-white/[0.06] px-6 md:px-10">
            {faqItems.map((it, i) => (
              <FaqAccordionItem key={i} q={it.q} a={it.a} idx={i} />
            ))}
          </div>
          <p className="font-body text-[11px] text-white/35 leading-relaxed mt-6 max-w-3xl">
            Tax statements are illustrative. IRE Berlin works with specialist tax advisors — your specific scenario is modelled before any commitment.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// ACT 3 — Interactive Calculator
// ═════════════════════════════════════════════════════════
const SliderField = ({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix,
  suffix,
  format = (v) => v.toLocaleString("de-DE"),
  testId,
  tone = "gold",
}) => {
  const pctFill = max > min ? Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100)) : 0;
  const track = tone === "gold" ? "linear-gradient(90deg,#D4AF6A 0%,#D4AF6A var(--p),rgba(255,255,255,0.08) var(--p),rgba(255,255,255,0.08) 100%)" : "linear-gradient(90deg,#5a9a7a 0%,#5a9a7a var(--p),rgba(255,255,255,0.08) var(--p),rgba(255,255,255,0.08) 100%)";
  return (
    <div className="flex flex-col" data-testid={testId ? `${testId}-wrap` : undefined}>
      <div className="flex items-baseline justify-between mb-1.5 gap-2">
        <label className="font-body text-[11px] tracking-[0.05em] text-white/75 truncate">
          {label}
          {hint && <span className="text-white/45 ml-1">· {hint}</span>}
        </label>
        <div className="font-mono text-xs text-ire-accent tabular-nums shrink-0">
          {prefix}{format(value)}{suffix}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={Number.isFinite(value) ? value : min}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testId}
        style={{ background: track, "--p": `${pctFill}%` }}
        className="ire-slider w-full h-1.5 rounded-full appearance-none cursor-pointer outline-none"
      />
    </div>
  );
};

const InputField = ({ label, hint, value, onChange, prefix, suffix, step = 1, testId }) => (
  <div className="flex flex-col">
    <label className="font-body text-[11px] tracking-[0.05em] text-white/70 mb-2">
      {label}
      {hint && <span className="text-white/50 ml-1">· {hint}</span>}
    </label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 font-mono text-xs">{prefix}</span>}
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        data-testid={testId}
        className={`w-full bg-black/40 border border-white/[0.08] text-white font-mono text-sm py-2.5 ${
          prefix ? "pl-8" : "pl-3"
        } ${suffix ? "pr-9" : "pr-3"} focus:outline-none focus:border-ire-accent/60 focus:bg-black/60 transition-all tabular-nums`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 font-mono text-xs">{suffix}</span>}
    </div>
  </div>
);

const InputGroup = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/[0.06] last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-heading text-[11px] tracking-[0.3em] text-white/80 uppercase">{title}</span>
        <ChevronDown className={`w-4 h-4 text-white/60 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 space-y-3">{children}</div>}
    </div>
  );
};

const KPI = ({ label, value, sub, tone = "neutral", large = false }) => {
  const toneClass = tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-rose-400" : "text-ire-accent";
  return (
    <div className="bg-black/40 border border-white/[0.06] p-5 md:p-6">
      <div className="font-body text-[10px] tracking-[0.25em] text-white/70 uppercase mb-2">{label}</div>
      <div className={`font-heading ${large ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"} ${toneClass} tabular-nums`}>{value}</div>
      {sub && <div className="font-body text-[11px] text-white/60 mt-1.5 leading-snug">{sub}</div>}
    </div>
  );
};

// ─── METRIC ROW — used in the headline-metrics panel (matches developer exposé layout)
const MetricRow = ({ label, value, sub, tone = "default", emphasis = false, testId }) => {
  const toneClass = tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-rose-400" : tone === "gold" ? "text-ire-accent" : "text-white";
  return (
    <div className={`p-5 md:p-6 ${emphasis ? "bg-ire-accent/[0.05]" : "bg-[#050505]"}`} data-testid={testId}>
      <div className="font-body text-[10px] tracking-[0.22em] text-white/70 uppercase mb-2 leading-snug min-h-[28px]">{label}</div>
      <div className={`font-heading text-2xl md:text-[28px] ${toneClass} tabular-nums leading-none`}>{value}</div>
      {sub && <div className="font-body text-[10px] text-white/50 mt-2 leading-snug">{sub}</div>}
    </div>
  );
};



// ═════════════════════════════════════════════════════════
// CALCULATOR — Story view + Detailed model
// ═════════════════════════════════════════════════════════

// ─── Animated number (counts up smoothly when value changes)
const AnimatedNumber = ({ value, formatter = (v) => v.toLocaleString("de-DE"), prefix = "", suffix = "", className = "" }) => {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const from = display;
    const to = value;
    if (Math.abs(to - from) < 0.001) return;
    const duration = 450;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span className={`tabular-nums ${className}`}>{prefix}{formatter(Math.round(display))}{suffix}</span>;
};

// ─── Plain-English hero answer card with glow on hover
const AnswerCard = ({ kicker, question, answer, sub, accent = "gold", icon, testId }) => {
  const accentBg = accent === "green" ? "from-emerald-500/10" : accent === "red" ? "from-rose-500/10" : "from-ire-accent/10";
  const accentText = accent === "green" ? "text-emerald-400" : accent === "red" ? "text-rose-400" : "text-ire-accent";
  const accentBorder = accent === "green" ? "border-emerald-500/30" : accent === "red" ? "border-rose-500/30" : "border-ire-accent/30";
  const accentGlow = accent === "green" ? "shadow-[0_0_60px_-20px_rgba(52,211,153,0.35)]" : accent === "red" ? "shadow-[0_0_60px_-20px_rgba(244,63,94,0.3)]" : "shadow-[0_0_60px_-20px_rgba(212,175,106,0.4)]";
  return (
    <div data-testid={testId} className={`relative bg-gradient-to-br ${accentBg} via-transparent to-transparent border ${accentBorder} p-7 md:p-8 group hover:${accentGlow} transition-all duration-500 overflow-hidden`}>
      {/* subtle grain */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")" }} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <span className="font-body text-[10px] tracking-[0.3em] text-white/55 uppercase">{kicker}</span>
        </div>
        <div className="font-heading text-base md:text-lg text-white/90 mb-5 leading-snug">{question}</div>
        <div className={`font-heading text-4xl md:text-5xl xl:text-6xl ${accentText} leading-none mb-3`}>{answer}</div>
        <div className="font-body text-sm text-white/65 leading-relaxed">{sub}</div>
      </div>
    </div>
  );
};

// ─── Sticky floating "Adjust inputs" pill (premium glow) — visible only when calculator is in view
const FloatingAdjustButton = ({ onClick, testId, sectionRef }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!sectionRef?.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.intersectionRatio > 0.05),
      { threshold: [0, 0.05, 0.5] }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [sectionRef]);

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
      style={{ pointerEvents: visible ? "auto" : "none" }}
      onClick={onClick}
      data-testid={testId}
      className="fixed bottom-7 right-7 z-40 hidden md:flex items-center gap-2.5 bg-ire-accent text-black font-body text-xs tracking-[0.18em] uppercase pl-4 pr-5 py-3.5 rounded-full shadow-[0_8px_40px_-8px_rgba(212,175,106,0.6)] hover:shadow-[0_12px_50px_-6px_rgba(212,175,106,0.85)] hover:scale-105 transition-shadow duration-300"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-black/60 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-black" />
      </span>
      Adjust inputs
    </motion.button>
  );
};

// ─── UNIT PREVIEW CARD — shows when a profile is selected (visualizes the unit)
const UnitPreview = ({ activeProfile, profiles = UNIT_PROFILES }) => {
  const [tab, setTab] = useState("plan"); // "plan" | "interior"
  const profile = profiles[activeProfile];
  if (!profile || activeProfile === "custom" || !profile.representative) {
    return (
      <div className="h-full bg-[#050505] border border-white/[0.06] border-dashed flex items-center justify-center p-6" data-testid="unit-preview-empty">
        <div className="text-center max-w-xs">
          <div className="font-body text-[10px] tracking-[0.3em] text-white/40 uppercase mb-2">Custom scenario</div>
          <div className="font-body text-xs text-white/55 leading-relaxed">Select a unit profile to see a floor plan + interior preview of what you'd be buying.</div>
        </div>
      </div>
    );
  }
  const src = tab === "plan" ? profile.representative : profile.rendering;
  return (
    <div className="h-full bg-[#050505] border border-ire-accent/20 overflow-hidden shadow-[0_0_50px_-20px_rgba(212,175,106,0.35)]" data-testid="unit-preview">
      {/* Tab pills */}
      <div className="flex items-stretch border-b border-white/[0.06]">
        <button
          onClick={() => setTab("plan")}
          data-testid="unit-preview-tab-plan"
          className={`flex-1 font-body text-[10px] tracking-[0.25em] uppercase py-2.5 transition-all ${
            tab === "plan" ? "bg-ire-accent/10 text-ire-accent" : "text-white/55 hover:text-white"
          }`}
        >
          Floor plan
        </button>
        <button
          onClick={() => setTab("interior")}
          data-testid="unit-preview-tab-interior"
          className={`flex-1 font-body text-[10px] tracking-[0.25em] uppercase py-2.5 transition-all ${
            tab === "interior" ? "bg-ire-accent/10 text-ire-accent" : "text-white/55 hover:text-white"
          }`}
        >
          Interior look
        </button>
      </div>
      {/* Image */}
      <div className={`relative aspect-[4/3] ${tab === "plan" ? "bg-white" : "bg-black"} overflow-hidden`}>
        <motion.img
          key={src}
          src={src}
          alt={profile.label}
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`absolute inset-0 w-full h-full ${tab === "plan" ? "object-contain p-3" : "object-cover"}`}
          loading="lazy"
        />
        {/* Representative label */}
        <div className="absolute top-2 left-2 bg-black/70 backdrop-blur px-2 py-1 font-body text-[9px] tracking-[0.2em] uppercase text-white/80">
          {tab === "plan" ? "Representative layout" : "Specification reference"}
        </div>
      </div>
      {/* Footer specs */}
      <div className="p-4 md:p-5 border-t border-white/[0.06]">
        <div className="font-body text-[10px] tracking-[0.25em] text-ire-accent uppercase mb-1.5">{profile.label.split(" — ")[0]}</div>
        <div className="font-heading text-base text-white mb-2.5 leading-snug">{profile.label.split(" — ")[1]}</div>
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          <div>
            <div className="text-white/45 tracking-wider uppercase">Rooms</div>
            <div className="text-white font-mono tabular-nums mt-0.5">{profile.rooms}</div>
          </div>
          <div>
            <div className="text-white/45 tracking-wider uppercase">Area</div>
            <div className="text-white font-mono tabular-nums mt-0.5">{profile.sqm} m²</div>
          </div>
          <div>
            <div className="text-white/45 tracking-wider uppercase">Floor</div>
            <div className="text-white font-mono mt-0.5">{profile.floor?.replace(" (3rd OG)", "").replace(" floor", "")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── INPUTS PANEL — premium slider-driven levers (matches reference exposé tool)
const InputsPanel = ({ inp, setInp, activeProfile, applyProfile, panelId, scenario, setScenario, profiles = UNIT_PROFILES, scenarios = SCENARIOS, stepLabel = "Step 1 · Inputs", title = "Calibrate your model" }) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [ekMode, setEkMode] = useState("ek"); // "ek" | "fk"
  const update = (k) => (v) => {
    setInp((p) => ({ ...p, [k]: v }));
  };
  const setPct = (k) => (v) => setInp((p) => ({ ...p, [k]: v / 100 }));

  // KfW add/remove
  const toggleKfw = () => {
    setInp((p) => {
      if (p.kfwEnabled) {
        return { ...p, kfwEnabled: false, l2Amount: 0 };
      }
      return { ...p, kfwEnabled: true, l2Amount: 150000, l2Rate: 0.0254, l2Tilg: 0.0183 };
    });
  };

  // Total acquisition cost (matches exposé math)
  const knkRate = inp.grunderwerb + inp.grundbuchKauf + inp.notar;
  const knk = inp.kpApt * knkRate + (inp.l1Amount + inp.l2Amount) * inp.pfand;
  const totalCost = inp.kpApt + inp.kpFurn + knk;
  const yourInvestment = inp.equity + knk;
  const totalLoan = inp.l1Amount + inp.l2Amount;
  // ekPct represents Your Investment (equity + knk) as % of totalCost — allows negative for over-financing
  const ekPct = totalCost > 0 ? (yourInvestment / totalCost) * 100 : 0;
  const fkPct = 100 - ekPct;

  return (
    <div id={panelId} className="relative bg-[#0a0a0a]/80 backdrop-blur-md border border-white/[0.08] p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <div className="font-heading text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-1">{stepLabel}</div>
          <div className="font-heading text-2xl md:text-3xl text-white">{title}</div>
          <div className="font-body text-sm text-white/65 mt-1">Drag the levers — every value updates the analysis below instantly. Matches the developer&apos;s official exposé math.</div>
        </div>
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45">
          {activeProfile === "custom" ? "● Custom scenario" : `● ${profiles[activeProfile]?.label || ""}`}
        </div>
      </div>

      {/* Profile + Scenario selectors */}
      <div className="grid sm:grid-cols-2 gap-4 mb-7">
        <div>
          <label className="font-body text-[10px] tracking-[0.25em] text-white/70 uppercase block mb-2">Unit profile</label>
          <div className="relative">
            <select
              value={activeProfile}
              onChange={(e) => applyProfile(e.target.value)}
              data-testid="unit-profile-selector"
              className="w-full appearance-none bg-black/50 border border-ire-accent/30 text-white font-body text-sm py-3 pl-3 pr-9 focus:outline-none focus:border-ire-accent transition-all cursor-pointer"
            >
              {Object.entries(profiles).map(([key, profile]) => (
                <option key={key} value={key} className="bg-[#0a0a0a] text-white">{profile.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ire-accent pointer-events-none" />
          </div>
          <div className="font-body text-[11px] text-white/55 mt-1.5 leading-snug">{profiles[activeProfile]?.sub}</div>
        </div>

        <div>
          <label className="font-body text-[10px] tracking-[0.25em] text-white/70 uppercase block mb-2">Scenario selection</label>
          <div className="relative">
            <select
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              data-testid="scenario-selector"
              className="w-full appearance-none bg-black/50 border border-white/[0.12] text-white font-body text-sm py-3 pl-3 pr-9 focus:outline-none focus:border-ire-accent transition-all cursor-pointer"
            >
              {Object.entries(scenarios).map(([key, s]) => (
                <option key={key} value={key} className="bg-[#0a0a0a] text-white">{s.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60 pointer-events-none" />
          </div>
          <div className="font-body text-[11px] text-white/55 mt-1.5 leading-snug">{scenarios[scenario]?.sub}</div>
        </div>
      </div>

      {/* ── YOUR INVESTMENT (live total) */}
      <div className="bg-gradient-to-br from-ire-accent/[0.08] via-transparent to-transparent border border-ire-accent/30 p-5 mb-6 shadow-[0_0_50px_-20px_rgba(212,175,106,0.4)]" data-testid="your-investment-block">
        <div className="flex items-center justify-between gap-3 mb-1">
          <span className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase">Your investment</span>
          <span className="font-mono text-[10px] tracking-[0.2em] text-white/50 uppercase">Out-of-pocket</span>
        </div>
        <div className="font-heading text-4xl md:text-5xl text-white tabular-nums" data-testid="your-investment-value">
          {fmtEUR(yourInvestment)}
        </div>
        <div className="font-body text-[11px] text-white/55 mt-1.5">
          Equity {fmtEUR(inp.equity)} + acquisition costs {fmtEUR(knk)}
        </div>
      </div>

      {/* ── PRIMARY LEVERS — sliders (match reference exposé tool) */}
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
        {/* Equity slider with EK%/FK% toggle */}
        <div className="md:col-span-2 bg-black/30 border border-white/[0.06] p-4">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="font-body text-[11px] tracking-[0.05em] text-white/75">
                {ekMode === "ek" ? "Your equity (incl. furniture)" : "Loan financing share"}
              </div>
              <div className="font-mono text-[10px] text-white/45 mt-0.5">{fmtEUR(inp.equity)} equity · {fmtEUR(totalLoan)} loans</div>
            </div>
            <button
              type="button"
              onClick={() => setEkMode((m) => (m === "ek" ? "fk" : "ek"))}
              data-testid="ek-fk-toggle"
              className="font-body text-[10px] tracking-[0.2em] uppercase text-ire-accent hover:text-white underline underline-offset-4 decoration-ire-accent/40"
            >
              Switch to {ekMode === "ek" ? "FK %" : "EK %"}
            </button>
          </div>
          <SliderField
            label={ekMode === "ek" ? "Equity share" : "Loan share"}
            value={ekMode === "ek" ? +ekPct.toFixed(2) : +fkPct.toFixed(2)}
            onChange={(v) => {
              const ek = ekMode === "ek" ? v : 100 - v;
              const newInvestment = (ek / 100) * totalCost; // can be negative → bank over-finances knk
              // Map slider value → state.equity (raw, can be negative) and l1Amount (grows when equity negative)
              const newEquity = newInvestment - knk; // ekPct of totalCost is "Your Investment"; subtract knk to get equity portion
              const newLoan1 = Math.max(0, totalCost - newEquity - inp.kpFurn - inp.l2Amount);
              setInp((p) => ({ ...p, equity: newEquity, l1Amount: newLoan1 }));
            }}
            min={-10}
            max={50}
            step={0.5}
            suffix=" %"
            format={(v) => v.toFixed(2).replace(".", ",")}
            testId="slider-equity"
          />
          {ekPct < 0 && (
            <div className="mt-2 font-body text-[11px] text-amber-300/90 leading-snug">
              Over-financed — bank also finances acquisition costs (notary, Grundbuch, transfer tax). Loan increases accordingly.
            </div>
          )}
        </div>

        {/* Purchase price */}
        <SliderField
          label="Purchase price"
          value={inp.kpApt}
          onChange={update("kpApt")}
          min={Math.max(50000, Math.round(inp.kpApt * 0.7))}
          max={Math.round(inp.kpApt * 1.3)}
          step={500}
          prefix="€ "
          format={(v) => Math.round(v).toLocaleString("de-DE")}
          testId="slider-kpApt"
        />

        {/* Monthly rent */}
        <SliderField
          label="Cold rent · per month"
          value={inp.rent}
          onChange={update("rent")}
          min={Math.max(200, Math.round(inp.rent * 0.5))}
          max={Math.round(inp.rent * 1.6)}
          step={5}
          prefix="€ "
          format={(v) => Math.round(v).toLocaleString("de-DE")}
          testId="slider-rent"
        />

        {/* Loan 1 Rate */}
        <SliderField
          label="Loan 1 — interest rate"
          value={+(inp.l1Rate * 100).toFixed(2)}
          onChange={setPct("l1Rate")}
          min={1}
          max={8}
          step={0.05}
          suffix=" %"
          format={(v) => v.toFixed(2).replace(".", ",")}
          testId="slider-l1Rate"
        />

        {/* Loan 1 Repayment */}
        <SliderField
          label="Loan 1 — repayment · 1st year"
          value={+(inp.l1Tilg * 100).toFixed(2)}
          onChange={setPct("l1Tilg")}
          min={0.5}
          max={5}
          step={0.05}
          suffix=" %"
          format={(v) => v.toFixed(2).replace(".", ",")}
          testId="slider-l1Tilg"
        />

        {/* Value growth */}
        <SliderField
          label="Value appreciation · per year"
          value={+(inp.valueGrowth * 100).toFixed(2)}
          onChange={setPct("valueGrowth")}
          min={0}
          max={6}
          step={0.1}
          suffix=" %"
          format={(v) => v.toFixed(2).replace(".", ",")}
          testId="slider-valueGrowth"
        />

        {/* Rent growth */}
        <SliderField
          label="Rent increase · per year"
          value={+(inp.rentGrowth * 100).toFixed(2)}
          onChange={setPct("rentGrowth")}
          min={0}
          max={5}
          step={0.1}
          suffix=" %"
          format={(v) => v.toFixed(2).replace(".", ",")}
          testId="slider-rentGrowth"
        />

        {/* Vacancy */}
        <SliderField
          label="Vacancy rate"
          value={+(inp.vacancy * 100).toFixed(2)}
          onChange={setPct("vacancy")}
          min={0}
          max={10}
          step={0.5}
          suffix=" %"
          format={(v) => v.toFixed(2).replace(".", ",")}
          testId="slider-vacancy"
        />

        {/* Tax bracket */}
        <SliderField
          label="Tax bracket (marginal)"
          value={+(inp.taxRate * 100).toFixed(0)}
          onChange={setPct("taxRate")}
          min={20}
          max={45}
          step={1}
          suffix=" %"
          format={(v) => `${Math.round(v)}`}
          testId="slider-taxRate"
        />
      </div>

      {/* Advanced toggle */}
      <button
        onClick={() => setAdvancedOpen(!advancedOpen)}
        data-testid="toggle-advanced-inputs"
        className="mt-7 flex items-center gap-2 font-body text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-ire-accent transition-colors"
      >
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${advancedOpen ? "rotate-180" : ""}`} />
        {advancedOpen ? "Hide" : "Show"} expert settings
        <span className="text-white/35 font-mono normal-case tracking-normal">· KfW, furniture, opex, construction, AfA</span>
      </button>

      <motion.div
        initial={false}
        animate={{ height: advancedOpen ? "auto" : 0, opacity: advancedOpen ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        {/* KfW module + Loan 2 — now inside the expert/collapsible drawer */}
        <div className="mt-7 pt-7 border-t border-white/[0.06]">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <div>
              <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-1">Further credit modules</div>
              <div className="font-heading text-base text-white">{inp.kfwEnabled ? "KfW Climate-Friendly New Build (QNG)" : "Bank financing only"}</div>
              {inp.kfwEnabled && (
                <div className="font-mono text-[11px] text-white/55 mt-1">
                  Programme: <span className="text-white/80">KfW Klimafreundlicher Neubau mit QNG</span> · 10-year fixed
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={toggleKfw}
              data-testid="kfw-toggle"
              className={`font-body text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-all ${
                inp.kfwEnabled
                  ? "border-rose-400/40 text-rose-300 hover:bg-rose-400/10"
                  : "border-ire-accent/50 text-ire-accent bg-ire-accent/5 hover:bg-ire-accent/15"
              }`}
            >
              {inp.kfwEnabled ? "− Remove KfW loan" : "+ Add KfW loan (€150k)"}
            </button>
          </div>

          {inp.kfwEnabled && (
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-4">
              <div className="flex items-baseline justify-between">
                <span className="font-body text-[11px] text-white/75">Loan amount</span>
                <span className="font-mono text-xs text-white tabular-nums">{fmtEUR(inp.l2Amount)}</span>
              </div>
              <SliderField
                label="KfW interest rate"
                value={+(inp.l2Rate * 100).toFixed(2)}
                onChange={setPct("l2Rate")}
                min={1}
                max={5}
                step={0.01}
                suffix=" %"
                format={(v) => v.toFixed(2).replace(".", ",")}
                testId="slider-l2Rate"
              />
              <SliderField
                label="KfW repayment · 1st year"
                value={+(inp.l2Tilg * 100).toFixed(2)}
                onChange={setPct("l2Tilg")}
                min={0}
                max={5}
                step={0.01}
                suffix=" %"
                format={(v) => v.toFixed(2).replace(".", ",")}
                testId="slider-l2Tilg"
              />
              <SliderField
                label="Grace years"
                value={inp.l2Grace}
                onChange={update("l2Grace")}
                min={0}
                max={10}
                step={1}
                suffix=" y"
                format={(v) => `${Math.round(v)}`}
                testId="slider-l2Grace"
              />
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-7 pt-7 border-t border-white/[0.06]">
          {/* Loan amounts (precise edit) */}
          <div>
            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-3">Loan amounts</div>
            <InputField label="Loan 1 amount" prefix="€" value={inp.l1Amount} onChange={update("l1Amount")} step={1000} testId="inp-l1Amount" />
            {inp.kfwEnabled && (
              <div className="mt-3">
                <InputField label="KfW amount" prefix="€" value={inp.l2Amount} onChange={update("l2Amount")} step={1000} testId="inp-l2Amount" />
              </div>
            )}
          </div>

          {/* Operating */}
          <div>
            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-3">Operating costs</div>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Service charge" hint="€/mo" prefix="€" value={inp.hausgeld} onChange={update("hausgeld")} testId="inp-hausgeld" />
              <InputField label="Property mgmt" hint="€/mo" prefix="€" value={inp.verwalt} onChange={update("verwalt")} testId="inp-verwalt" />
              <InputField label="Land share" suffix="%" value={+(inp.landShare * 100).toFixed(1)} onChange={(v) => setInp((p) => ({ ...p, landShare: v / 100 }))} step={0.5} testId="inp-landShare" />
              <InputField label="Hold years" value={inp.holding} onChange={update("holding")} testId="inp-holding" />
            </div>
          </div>

          {/* Construction */}
          <div>
            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-3">Construction phase</div>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Months left" value={inp.constrMonths} onChange={update("constrMonths")} testId="inp-constrMonths" />
              <InputField label="Avg drawn" suffix="%" value={+(inp.constrDrawPct * 100).toFixed(0)} onChange={(v) => setInp((p) => ({ ...p, constrDrawPct: v / 100 }))} step={5} testId="inp-constrDrawPct" />
              <InputField label="Furniture" prefix="€" value={inp.kpFurn} onChange={update("kpFurn")} step={500} testId="inp-kpFurn" />
              <InputField label="Equity (€)" prefix="€" value={inp.equity} onChange={update("equity")} step={1000} testId="inp-equity" />
            </div>
          </div>

          {/* Acquisition cost % */}
          <div>
            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-3">Acquisition costs</div>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Land transfer" suffix="%" value={+(inp.grunderwerb * 100).toFixed(2)} onChange={(v) => setInp((p) => ({ ...p, grunderwerb: v / 100 }))} step={0.01} testId="inp-grunderwerb" />
              <InputField label="Notary" suffix="%" value={+(inp.notar * 100).toFixed(2)} onChange={(v) => setInp((p) => ({ ...p, notar: v / 100 }))} step={0.01} testId="inp-notar" />
              <InputField label="Grundbuch buy" suffix="%" value={+(inp.grundbuchKauf * 100).toFixed(2)} onChange={(v) => setInp((p) => ({ ...p, grundbuchKauf: v / 100 }))} step={0.01} testId="inp-grundbuchKauf" />
              <InputField label="Pfandrecht" suffix="%" value={+(inp.pfand * 100).toFixed(2)} onChange={(v) => setInp((p) => ({ ...p, pfand: v / 100 }))} step={0.01} testId="inp-pfand" />
            </div>
          </div>

          {/* Depreciation */}
          <div>
            <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-3">Depreciation toggles</div>
            <label className="flex items-start gap-2.5 text-xs text-white/80 cursor-pointer p-2.5 bg-black/30 border border-white/[0.06] hover:border-ire-accent/30 transition-colors">
              <input
                type="checkbox"
                checked={inp.useDegressive}
                onChange={(e) => setInp((p) => ({ ...p, useDegressive: e.target.checked }))}
                data-testid="inp-degressive"
                className="accent-ire-accent mt-0.5"
              />
              <div>
                <div className="font-heading text-white">Degressive AfA (5%)</div>
                <div className="text-white/55 text-[11px] mt-0.5">Declining balance depreciation</div>
              </div>
            </label>
            <label className="flex items-start gap-2.5 text-xs text-white/80 cursor-pointer p-2.5 bg-black/30 border border-white/[0.06] hover:border-ire-accent/30 transition-colors mt-2">
              <input
                type="checkbox"
                checked={inp.useSonder7b}
                onChange={(e) => setInp((p) => ({ ...p, useSonder7b: e.target.checked }))}
                data-testid="inp-sonder7b"
                className="accent-ire-accent mt-0.5"
              />
              <div>
                <div className="font-heading text-white">§7b Sonder-AfA</div>
                <div className="text-white/55 text-[11px] mt-0.5">+5% × 4 years (capped €4,000/m²)</div>
              </div>
            </label>
          </div>
        </div>
      </motion.div>

      {/* Disclaimer */}
      <div className="mt-7 pt-5 border-t border-white/[0.06] font-body text-[11px] text-white/45 leading-relaxed">
        Subject to changes &amp; input errors. Interest rate fixation for 10 years (loan term possibly longer).
        Future value and rent increases are not guaranteed. Inputs that deviate from the standard scenario
        influence the investment calculation — please consult your advisor.
      </div>
    </div>
  );
};

// ─── CASHFLOW JOURNEY — simplified single-line area chart
const CashflowJourney = ({ r, inp }) => {
  // Build per-month cashflow (after tax) over the full hold
  const grace = Math.max(0, Math.floor(inp.l2Grace));
  const data = r.rows.map((row) => ({
    year: `Y${row.year}`,
    monthly: Math.round(row.netCFAfterTax / 12),
  }));
  const maxAbs = Math.max(...data.map(d => Math.abs(d.monthly)));
  const yPad = Math.ceil(maxAbs * 1.2);

  return (
    <div className="bg-[#0a0a0a] border border-white/[0.06] p-7 md:p-8 relative overflow-hidden">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
        <div>
          <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Your monthly journey</div>
          <h3 className="font-heading text-2xl md:text-3xl text-white">Will it pay for itself?</h3>
          <div className="font-body text-sm text-white/70 mt-1.5 max-w-2xl">
            What you'd put in <em>or</em> take out, every month — after rent, mortgage, costs and the tax effect.
          </div>
        </div>
      </div>

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="cfPos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D4AF6A" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#D4AF6A" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="cfNeg" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#8B4A4A" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#8B4A4A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={chartTheme.grid} vertical={false} />
            <XAxis dataKey="year" stroke={chartTheme.axis} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.8)" }} tickLine={false} axisLine={{ stroke: chartTheme.grid }} />
            <YAxis stroke={chartTheme.axis} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.75)" }} tickLine={false} axisLine={false} domain={[-yPad, yPad]} tickFormatter={(v) => `${v >= 0 ? "+" : ""}${v}€`} />
            <Tooltip
              cursor={{ stroke: chartTheme.accent, strokeOpacity: 0.4 }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const v = payload[0].value;
                return (
                  <div className="bg-[#0a0a0a]/95 backdrop-blur-md border border-ire-accent/20 px-4 py-3 text-xs">
                    <div className="font-heading text-white/70 mb-1">{label}</div>
                    <div className={`font-mono tabular-nums ${v >= 0 ? "text-emerald-400" : "text-rose-400"} text-base`}>{v >= 0 ? "+" : ""}{v.toLocaleString("de-DE")} €/mo</div>
                    <div className="text-white/55 mt-1">{v >= 0 ? "you earn this each month" : "you put in this each month"}</div>
                  </div>
                );
              }}
            />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" strokeDasharray="2 4" />
            <Area type="monotone" dataKey="monthly" stroke={chartTheme.accent} strokeWidth={2.5} fill="url(#cfPos)" baseValue={0} dot={{ r: 4, fill: chartTheme.accent, stroke: "#0a0a0a", strokeWidth: 2 }} activeDot={{ r: 6 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Plain-English milestones */}
      <div className="grid sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/[0.06]">
        <div className="text-xs">
          <div className="font-body text-[10px] tracking-[0.25em] text-ire-accent uppercase mb-1.5">Years 1–4</div>
          <div className="text-white/85 leading-relaxed"><span className="text-emerald-400 font-semibold">Best years.</span> The §7b tax break is active — you collect a big refund each year.</div>
        </div>
        <div className="text-xs">
          <div className="font-body text-[10px] tracking-[0.25em] text-amber-400 uppercase mb-1.5">Year 5</div>
          <div className="text-white/85 leading-relaxed"><span className="text-amber-300 font-semibold">§7b expires.</span> Tax refund shrinks. KfW grace also ends — Loan 2 starts repaying.</div>
        </div>
        <div className="text-xs">
          <div className="font-body text-[10px] tracking-[0.25em] text-white/65 uppercase mb-1.5">Years 6–{inp.holding}</div>
          <div className="text-white/85 leading-relaxed"><span className="text-white font-semibold">Hold &amp; grow.</span> Your monthly cost stabilises while the property keeps appreciating and your tenants pay down debt.</div>
        </div>
      </div>
    </div>
  );
};

// ─── WEALTH WATERFALL (refined)
const WealthWaterfall = ({ r, inp }) => {
  const inv = r.investment;
  const sale = r.valueEnd;
  const payoff = r.finalBalance;
  const cumCF = r.cumNetCFAfterTax;
  let running = 0;
  const wf = [];
  wf.push({ label: "You put in", base: 0, value: -inv, color: chartTheme.negative, display: -inv });
  running = -inv;
  wf.push({ label: "Sell the property", base: running, value: sale, color: chartTheme.accent, display: sale });
  running += sale;
  wf.push({ label: "Pay off the loan", base: running, value: -payoff, color: chartTheme.negative, display: -payoff });
  running -= payoff;
  wf.push({ label: "Cashflow over 10y", base: running, value: cumCF, color: cumCF >= 0 ? "#5a9a7a" : chartTheme.negative, display: cumCF });
  wf.push({ label: "You walk away with", base: 0, value: r.wealth, color: chartTheme.accent, display: r.wealth, isTotal: true });

  return (
    <div className="bg-[#0a0a0a] border border-white/[0.06] p-7 md:p-8" data-testid="wealth-waterfall">
      <div className="mb-6">
        <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">10-year wealth picture</div>
        <h3 className="font-heading text-2xl md:text-3xl text-white">Your wealth at Year {inp.holding}</h3>
        <div className="font-body text-sm text-white/70 mt-1.5">Tax-free after the 10-year holding period (§23 EStG).</div>
      </div>
      <div className="h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={wf} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
            <CartesianGrid stroke={chartTheme.grid} vertical={false} />
            <XAxis dataKey="label" stroke={chartTheme.axis} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.85)" }} tickLine={false} axisLine={{ stroke: chartTheme.grid }} interval={0} />
            <YAxis stroke={chartTheme.axis} tick={{ fontSize: 10, fill: "rgba(255,255,255,0.7)" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip
              cursor={{ fill: "rgba(212,175,106,0.05)" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-[#0a0a0a]/95 backdrop-blur-md border border-ire-accent/20 px-4 py-3 text-xs">
                    <div className="font-heading text-white mb-1">{d.label}</div>
                    <div className={`font-mono tabular-nums text-base ${d.display >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{d.display >= 0 ? "+" : ""}{fmtEUR(d.display)}</div>
                  </div>
                );
              }}
            />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
            <Bar dataKey="base" stackId="wf" fill="transparent" />
            <Bar dataKey="value" stackId="wf" radius={[3, 3, 0, 0]}>
              {wf.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Plain English narration */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06] mt-6">
        <div className="bg-black/40 p-5">
          <div className="font-body text-[10px] tracking-[0.25em] text-white/70 uppercase mb-2">Property grew</div>
          <div className="font-heading text-xl md:text-2xl text-ire-accent tabular-nums">+{fmtEUR(sale - inp.kpApt)}</div>
          <div className="font-body text-[11px] text-white/65 mt-1">{fmtPct(((sale / inp.kpApt) - 1) * 100, 1)} total over {inp.holding}y</div>
        </div>
        <div className="bg-black/40 p-5">
          <div className="font-body text-[10px] tracking-[0.25em] text-white/70 uppercase mb-2">Tenants paid down</div>
          <div className="font-heading text-xl md:text-2xl text-ire-accent tabular-nums">+{fmtEUR(r.totalLoan - r.finalBalance)}</div>
          <div className="font-body text-[11px] text-white/65 mt-1">Equity built by rent</div>
        </div>
        <div className="bg-black/40 p-5">
          <div className="font-body text-[10px] tracking-[0.25em] text-white/70 uppercase mb-2">Cashflow during hold</div>
          <div className={`font-heading text-xl md:text-2xl tabular-nums ${cumCF >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{cumCF >= 0 ? "+" : ""}{fmtEUR(cumCF)}</div>
          <div className="font-body text-[11px] text-white/65 mt-1">After-tax, {inp.holding}-year sum</div>
        </div>
        <div className="bg-ire-accent/[0.06] p-5 border-l border-ire-accent/30 shadow-[inset_0_0_30px_-10px_rgba(212,175,106,0.25)]" data-testid="multiple-on-capital">
          <div className="font-body text-[10px] tracking-[0.25em] text-ire-accent uppercase mb-2">Your money multiplied</div>
          <div className="font-heading text-xl md:text-2xl text-ire-accent tabular-nums">{inv > 0 ? (r.wealth / inv).toFixed(1) : "∞"}×</div>
          <div className="font-body text-[11px] text-white/65 mt-1">{fmtEUR(r.wealth)} on {fmtEUR(inv)} invested</div>
        </div>
      </div>
    </div>
  );
};

// ─── MONEY DONUTS
const MoneyDonuts = ({ r, inp }) => {
  const acquisitionFees = r.grunderwerbCost + r.grundbuchKaufCost + r.notarCost + r.pfandCost;
  const costData = [
    { name: "Apartment", value: inp.kpApt, color: chartTheme.accent },
    { name: "Furniture", value: inp.kpFurn, color: "#8a7344" },
    { name: "Acquisition fees", value: acquisitionFees, color: chartTheme.neutral },
  ];
  const totalCost = inp.kpApt + inp.kpFurn + acquisitionFees;
  const fundingData = [
    { name: "Your equity + furniture (out-of-pocket)", value: Math.max(r.investment + inp.kpFurn, 1), color: chartTheme.accent },
    { name: "Loan 1 (bank)", value: inp.l1Amount, color: chartTheme.neutral },
    { name: "Loan 2 (KfW)", value: inp.l2Amount, color: "#6a7785" },
  ];
  const totalFunding = r.investment + inp.kpFurn + r.totalLoan;
  const Donut = ({ title, data, total }) => (
    <div>
      <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-4">{title}</div>
      <div className="flex items-center gap-6 flex-wrap">
        <div className="relative w-[200px] h-[200px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value" stroke="none">
                {data.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="font-mono text-[10px] text-white/65 tracking-widest uppercase">Total</div>
            <div className="font-heading text-lg text-white tabular-nums mt-0.5">{fmtEUR(total)}</div>
          </div>
        </div>
        <div className="flex-1 min-w-[200px] space-y-3">
          {data.map((d) => (
            <div key={d.name} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2.5 h-2.5 shrink-0" style={{ background: d.color }} />
                <span className="text-white/90 truncate">{d.name}</span>
              </div>
              <div className="text-right shrink-0 ml-3">
                <span className="font-mono text-white tabular-nums">{fmtEUR(d.value)}</span>
                <span className="font-mono text-white/55 ml-2 text-[10px]">{fmtPct((d.value / total) * 100, 0)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  return (
    <div className="bg-[#0a0a0a] border border-white/[0.06] p-7 md:p-8" data-testid="cost-breakdown">
      <div className="mb-6">
        <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Where every euro goes</div>
        <h3 className="font-heading text-2xl md:text-3xl text-white">Cost composition & funding mix</h3>
        <div className="font-body text-sm text-white/70 mt-1.5">Grand total {fmtEUR(totalCost)} — fully funded between your out-of-pocket and the two loans.</div>
      </div>
      <div className="grid md:grid-cols-2 gap-8 md:gap-12">
        <Donut title="Cost composition" data={costData} total={totalCost} />
        <Donut title="How it's funded" data={fundingData} total={totalFunding} />
      </div>
    </div>
  );
};

// ─── DETAILED CONSULTANT MATRIX
const DetailedMatrix = ({ r, inp }) => {
  const grace = Math.max(0, Math.floor(inp.l2Grace));
  const sumYears = (mapper) => r.rows.reduce((a, row) => a + mapper(row), 0);
  const propValPerYear = (y) => inp.kpApt * Math.pow(1 + inp.valueGrowth, y);
  const YearTH = ({ y, children }) => (
    <th className={`py-3 px-2 text-right font-body text-[10px] tracking-[0.15em] uppercase ${y && y <= grace ? "bg-amber-500/[0.08] text-amber-300/95" : "text-white/80"}`}>{children}</th>
  );
  const YearTD = ({ y, tone = "default", italic = false, children }) => {
    let cls = "text-white/90";
    if (italic) cls = "text-white/65 italic";
    else if (tone === "pos") cls = "text-emerald-400/90";
    else if (tone === "neg") cls = "text-rose-400/90";
    else if (tone === "bold-pos") cls = "font-semibold text-emerald-400";
    else if (tone === "bold-neg") cls = "font-semibold text-rose-400";
    return (
      <td className={`py-2.5 px-2 text-right tabular-nums ${cls} ${y && y <= grace ? "bg-amber-500/[0.05]" : ""}`}>{children}</td>
    );
  };
  const SectionRow = ({ label }) => (
    <tr className="border-t border-b border-white/[0.06] bg-white/[0.025]">
      <td colSpan={inp.holding + 2} className="py-2.5 px-2 font-body text-[10px] tracking-[0.2em] uppercase text-ire-accent">{label}</td>
    </tr>
  );
  return (
    <div className="bg-[#0a0a0a] border border-white/[0.06] p-6 md:p-7" data-testid="detailed-matrix">
      <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
        <div>
          <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Full {inp.holding}-year cashflow</div>
          <h3 className="font-heading text-2xl md:text-3xl text-white">Detailed model</h3>
          <div className="font-body text-sm text-white/70 mt-1.5">Every line your tax advisor needs. <span className="text-amber-300/85">Amber columns</span> are grace-period years.</div>
        </div>
        <div className="font-mono text-xs text-white/60">All values in €/year</div>
      </div>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="py-3 px-2 text-left font-body text-[10px] tracking-[0.15em] uppercase text-white/80 min-w-[200px]">&nbsp;</th>
              {r.rows.map((row) => <YearTH key={row.year} y={row.year}>Y{row.year}</YearTH>)}
              <YearTH>Σ {inp.holding}Y</YearTH>
            </tr>
          </thead>
          <tbody>
            <SectionRow label="Income" />
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/90">Rent (annual)</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year}>{fmtEUR(row.rent)}</YearTD>)}
              <YearTD>{fmtEUR(sumYears((r2) => r2.rent))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/90">Operating costs</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone="neg">−{fmtEUR(row.opCosts)}</YearTD>)}
              <YearTD tone="neg">−{fmtEUR(sumYears((r2) => r2.opCosts))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]" style={{ fontWeight: 600 }}>
              <td className="py-2.5 px-2 text-white">Operating cashflow</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year}>{fmtEUR(row.operatingCF)}</YearTD>)}
              <YearTD>{fmtEUR(sumYears((r2) => r2.operatingCF))}</YearTD>
            </tr>

            <SectionRow label="Financing" />
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/90">Interest & repayment</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone="neg">−{fmtEUR(row.debtService)}</YearTD>)}
              <YearTD tone="neg">−{fmtEUR(sumYears((r2) => r2.debtService))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/65 italic pl-6">of which interest</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} italic>−{fmtEUR(row.interest)}</YearTD>)}
              <YearTD italic>−{fmtEUR(sumYears((r2) => r2.interest))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/65 italic pl-6">of which principal</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} italic>−{fmtEUR(row.tilgung)}</YearTD>)}
              <YearTD italic>−{fmtEUR(sumYears((r2) => r2.tilgung))}</YearTD>
            </tr>

            <SectionRow label="Cashflow before tax" />
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white font-semibold">Net CF before tax</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone={row.netCFBeforeTax >= 0 ? "bold-pos" : "bold-neg"}>{fmtEUR(row.netCFBeforeTax)}</YearTD>)}
              <YearTD tone={sumYears((r2) => r2.netCFBeforeTax) >= 0 ? "bold-pos" : "bold-neg"}>{fmtEUR(sumYears((r2) => r2.netCFBeforeTax))}</YearTD>
            </tr>

            <SectionRow label="Tax effect" />
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/65 italic pl-6">Depreciation (degressive)</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} italic>−{fmtEUR(row.depDegressive)}</YearTD>)}
              <YearTD italic>−{fmtEUR(sumYears((r2) => r2.depDegressive))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/65 italic pl-6">Depreciation §7b</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} italic>{row.depSonder7b > 0 ? `−${fmtEUR(row.depSonder7b)}` : "0"}</YearTD>)}
              <YearTD italic>−{fmtEUR(sumYears((r2) => r2.depSonder7b))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/65 italic pl-6">Depreciation furniture</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} italic>{row.depFurniture > 0 ? `−${fmtEUR(row.depFurniture)}` : "0"}</YearTD>)}
              <YearTD italic>−{fmtEUR(sumYears((r2) => r2.depFurniture))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/90">Taxable result</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone={row.taxableIncome >= 0 ? "default" : "neg"}>{fmtEUR(row.taxableIncome)}</YearTD>)}
              <YearTD tone="neg">{fmtEUR(sumYears((r2) => r2.taxableIncome))}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/90">Tax saving (+) / burden (−)</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone={row.taxEffect >= 0 ? "pos" : "neg"}>{row.taxEffect >= 0 ? "+" : ""}{fmtEUR(row.taxEffect)}</YearTD>)}
              <YearTD tone={sumYears((r2) => r2.taxEffect) >= 0 ? "pos" : "neg"}>{sumYears((r2) => r2.taxEffect) >= 0 ? "+" : ""}{fmtEUR(sumYears((r2) => r2.taxEffect))}</YearTD>
            </tr>

            <SectionRow label="Net cashflow after tax" />
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white font-semibold">Net CF after tax</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone={row.netCFAfterTax >= 0 ? "bold-pos" : "bold-neg"}>{fmtEUR(row.netCFAfterTax)}</YearTD>)}
              <YearTD tone={r.cumNetCFAfterTax >= 0 ? "bold-pos" : "bold-neg"}>{fmtEUR(r.cumNetCFAfterTax)}</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/70 italic pl-6">per month</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} italic>{Math.round(row.netCFAfterTax / 12).toLocaleString("de-DE")}</YearTD>)}
              <YearTD italic>&nbsp;</YearTD>
            </tr>

            <SectionRow label="Wealth position (year-end)" />
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/90">Property value</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year}>{fmtEUR(propValPerYear(row.year))}</YearTD>)}
              <YearTD>&nbsp;</YearTD>
            </tr>
            <tr className="border-b border-white/[0.04]">
              <td className="py-2.5 px-2 text-white/90">Remaining debt</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone="neg">{fmtEUR(row.balanceEnd)}</YearTD>)}
              <YearTD>&nbsp;</YearTD>
            </tr>
            <tr>
              <td className="py-2.5 px-2 text-white font-semibold">Net equity in property</td>
              {r.rows.map((row) => <YearTD key={row.year} y={row.year} tone="pos">{fmtEUR(propValPerYear(row.year) - row.balanceEnd)}</YearTD>)}
              <YearTD>&nbsp;</YearTD>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Exit scenario */}
      <div className="mt-8 pt-7 border-t border-white/[0.08]" data-testid="exit-scenario">
        <h4 className="font-heading text-lg md:text-xl text-white mb-1">Exit scenario — sale at end of Year {inp.holding}</h4>
        <div className="font-body text-xs text-white/65 mb-5">Tax-free after 10y holding period (§23 EStG)</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-px bg-white/[0.06]">
          <KPI label="Property sale value" value={fmtEUR(r.valueEnd)} sub={`+${fmtPct(inp.valueGrowth * 100, 1)} p.a.`} />
          <KPI label="Loan payoff" value={`−${fmtEUR(r.finalBalance)}`} tone="neg" />
          <KPI label="Net sale proceeds" value={fmtEUR(r.saleProceedsAfterDebt)} tone="pos" />
          <KPI label="Cumulative cashflow" value={fmtEUR(r.cumNetCFAfterTax)} tone={r.cumNetCFAfterTax >= 0 ? "pos" : "neg"} sub={`${inp.holding}-yr sum`} />
          <KPI label="Total wealth gain" value={fmtEUR(r.wealth)} tone="pos" sub={`vs. ${fmtEUR(r.investment)} invested`} />
          <KPI label="Equity IRR after tax" value={fmtPct(r.equityIRR * 100)} tone={r.equityIRR >= 0 ? "pos" : "neg"} sub="Internal rate of return" />
        </div>
      </div>
    </div>
  );
};

const CalculatorAct = ({
  projectData = { defaults: KARLS_DEFAULTS, profiles: UNIT_PROFILES, scenarios: SCENARIOS },
  defaultProfileKey = "we08",
  defaultScenarioKey = "realistisch",
  act = "Act III",
  kicker = "Step into the math",
  headline = (
    <>
      <span>Is this the right unit?</span><br />
      <span className="text-ire-accent italic">And what does it actually do for you?</span>
    </>
  ),
} = {}) => {
  const navigate = useNavigate();
  const { defaults, profiles, scenarios } = projectData;
  const initialOverride = profiles[defaultProfileKey]?.overrides || {};
  const [inp, setInp] = useState({ ...defaults, ...initialOverride });
  const [activeProfile, setActiveProfile] = useState(defaultProfileKey);
  const [scenario, setScenarioState] = useState(defaultScenarioKey);
  const [view, setView] = useState("story"); // "story" | "detailed"
  const [inputsPanelId] = useState("inputs-panel");

  // Reset profile to custom on any manual input edit
  const wrappedSetInp = (updater) => {
    setInp((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setActiveProfile("custom");
      // If user edits growth/vacancy directly, flip scenario to individuell
      if (next.valueGrowth !== prev.valueGrowth || next.rentGrowth !== prev.rentGrowth || next.vacancy !== prev.vacancy) {
        setScenarioState("individuell");
      }
      return next;
    });
  };

  const applyProfile = (key) => {
    setActiveProfile(key);
    if (key === "custom") return;
    const profile = profiles[key];
    if (!profile) return;
    setInp((p) => ({ ...p, ...profile.overrides }));
  };

  const setScenario = (key) => {
    setScenarioState(key);
    const s = scenarios[key];
    if (s?.overrides) {
      setInp((p) => ({ ...p, ...s.overrides }));
    }
  };

  const r = useMemo(() => calculate(inp), [inp]);

  // Plain-English answer-card values
  const monthlyAT = r.monthlyCFAfterTaxY1;
  const cfAnswer = monthlyAT >= 0
    ? { kicker: "Will it pay for itself?", question: "Each month while you hold it, you " + (monthlyAT >= 50 ? "earn" : "roughly break even"), answer: `${monthlyAT >= 0 ? "+" : ""}${Math.round(monthlyAT).toLocaleString("de-DE")} €/mo`, sub: "Year-1 net after rent, debt service, costs and your tax refund.", accent: "green" }
    : { kicker: "Will it pay for itself?", question: "Each month while you hold it, you put in", answer: `${Math.round(monthlyAT).toLocaleString("de-DE")} €/mo`, sub: "Year-1 net after rent, debt service, costs and your tax refund. Typical for higher-LTV builds.", accent: "red" };

  const wealthMultiple = r.investment > 0 ? r.wealth / r.investment : 0;
  const sectionRef = useRef(null);

  const scrollToInputs = () => {
    const el = document.getElementById(inputsPanelId);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 90; // offset for sticky nav
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section ref={sectionRef} className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04]">
      <div className="max-w-[1600px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">{act}</span>
            <div className="h-px w-12 bg-ire-accent/40" />
            <span className="font-body text-[11px] tracking-[0.4em] text-white/45 uppercase">{kicker}</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-6 max-w-4xl">
            {headline}
          </h2>
          <p className="font-body text-base sm:text-lg text-white/75 max-w-3xl leading-relaxed mb-12">
            Pick a unit profile, fine-tune any number, and see exactly what the next 10 years look like — month by month, in plain English. Switch to the detailed model for the full {inp.holding}-year matrix.
          </p>
        </Reveal>

        {/* INPUTS PANEL — full width, top */}
        <Reveal delay={0.05}>
          <div className="mb-10 grid lg:grid-cols-[1fr_360px] gap-6">
            <InputsPanel
              inp={inp}
              setInp={wrappedSetInp}
              activeProfile={activeProfile}
              applyProfile={applyProfile}
              panelId={inputsPanelId}
              scenario={scenario}
              setScenario={setScenario}
              profiles={profiles}
              scenarios={scenarios}
            />
            <UnitPreview activeProfile={activeProfile} profiles={profiles} />
          </div>
        </Reveal>

        {/* TAB BAR */}
        <Reveal delay={0.1}>
          <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
            <div className="inline-flex items-stretch bg-[#0a0a0a] border border-white/[0.08] p-1">
              <button
                onClick={() => setView("story")}
                data-testid="view-tab-story"
                className={`relative font-body text-xs tracking-[0.2em] uppercase px-6 py-3 transition-all ${
                  view === "story" ? "text-black bg-ire-accent" : "text-white/70 hover:text-white"
                }`}
              >
                Story view
              </button>
              <button
                onClick={() => setView("detailed")}
                data-testid="view-tab-detailed"
                className={`relative font-body text-xs tracking-[0.2em] uppercase px-6 py-3 transition-all ${
                  view === "detailed" ? "text-black bg-ire-accent" : "text-white/70 hover:text-white"
                }`}
              >
                Detailed model
              </button>
            </div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45">
              {view === "story" ? "Plain-English answers, simple visuals" : "Full 10-year matrix for your tax advisor"}
            </div>
          </div>
        </Reveal>

        {/* CONTENT — STORY VIEW */}
        {view === "story" && (
          <motion.div
            key="story"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-7"
          >
            {/* ── HEADLINE METRICS — mirrors the developer exposé "Ihre Zahlen" page ── */}
            <div className="bg-[#0a0a0a] border border-ire-accent/20 shadow-[0_0_60px_-25px_rgba(212,175,106,0.35)]" data-testid="headline-metrics">
              <div className="px-6 md:px-7 pt-6 md:pt-7 pb-4 border-b border-white/[0.06] flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Your numbers</div>
                  <h3 className="font-heading text-2xl md:text-3xl text-white">Headline metrics</h3>
                </div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45">{profiles[activeProfile]?.label || "Custom"} · {scenarios[scenario]?.label}</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/[0.06]">
                <MetricRow label="Price per m²" value={fmtEUR(inp.kpApt / inp.sqm)} sub={`${inp.sqm.toLocaleString("de-DE")} m²`} testId="metric-pricepersqm" />
                <MetricRow label="Cold rent · per month" value={fmtEUR(inp.rent)} sub={`${(inp.rent / inp.sqm).toFixed(2).replace(".", ",")} €/m²`} testId="metric-rent" />
                <MetricRow label="Gross yield" value={fmtPct(r.grossYield * 100)} sub="Annual rent ÷ apartment price" testId="metric-gross-yield" />
                <MetricRow label="Net yield · 1st year" value={fmtPct(r.netYield * 100)} sub="After opex, before tax" testId="metric-net-yield" />
                <MetricRow
                  label="Net cashflow · p.M (Y1)"
                  value={`${r.monthlyCFY1 >= 0 ? "+" : ""}${fmtEUR(Math.round(r.monthlyCFY1))}`}
                  sub="Before tax"
                  tone={r.monthlyCFY1 >= 0 ? "pos" : "neg"}
                  testId="metric-cf-before-tax"
                />
                <MetricRow
                  label="Net cashflow n. Steuer · p.M (Y1)"
                  value={`${r.monthlyCFAfterTaxY1 >= 0 ? "+" : ""}${fmtEUR(Math.round(r.monthlyCFAfterTaxY1))}`}
                  sub="After §7b + degressive AfA"
                  tone={r.monthlyCFAfterTaxY1 >= 0 ? "pos" : "neg"}
                  testId="metric-cf-after-tax"
                />
                <MetricRow
                  label={`Total capital return (${inp.holding}y)`}
                  value={fmtPct((r.wealth / (r.investment + r.totalLoan)) * 100 / inp.holding)}
                  sub={`Across ${fmtEUR(r.investment + r.totalLoan)} deployed`}
                  testId="metric-total-capital-return"
                />
                <MetricRow
                  label={`CAGR on equity (${inp.holding}y · after tax)`}
                  value={fmtPct(r.ekVerzinsungExpose * 100)}
                  sub="Compound annual growth · exposé-style"
                  tone="gold"
                  testId="metric-ek-verzinsung-expose"
                />
                <MetricRow
                  label={`Equity IRR (${inp.holding}y · after tax)`}
                  value={fmtPct(r.equityIRR * 100)}
                  sub="Time-weighted true IRR"
                  tone="gold"
                  emphasis
                  testId="metric-equity-irr"
                />
              </div>
              <div className="px-6 md:px-7 py-3 border-t border-white/[0.06] font-body text-[10px] tracking-[0.05em] text-white/45 leading-relaxed">
                * For an individual tax assessment, please consult your tax advisor. ** After KfW repayment subsidy.
              </div>
            </div>

            {/* THREE BIG ANSWERS */}
            <div className="grid md:grid-cols-3 gap-5 md:gap-6" data-testid="three-answers">
              <AnswerCard
                kicker={cfAnswer.kicker}
                question={cfAnswer.question}
                answer={cfAnswer.answer}
                sub={cfAnswer.sub}
                accent={cfAnswer.accent}
                testId="answer-monthly"
              />
              <AnswerCard
                kicker="What it's worth in 10 years"
                question={`Sell at Year ${inp.holding}, pay off the loan, and your net property equity is`}
                answer={fmtEUR(r.saleProceedsAfterDebt)}
                sub={`Property value ${fmtEUR(r.valueEnd)} − remaining debt ${fmtEUR(r.finalBalance)}. Tax-free after the §23 EStG 10-year rule.`}
                accent="gold"
                testId="answer-equity"
              />
              <AnswerCard
                kicker="How much wealth you build"
                question="On the cash you put in, your total return is"
                answer={r.investment > 0 ? `${wealthMultiple.toFixed(1)}×` : "—"}
                sub={`${fmtEUR(r.wealth)} on ${fmtEUR(r.investment)} invested · ${fmtPct(r.equityIRR * 100, 2)} IRR after tax.`}
                accent="gold"
                testId="answer-multiple"
              />
            </div>

            {/* THE JOURNEY CHART */}
            <CashflowJourney r={r} inp={inp} />

            {/* WEALTH WATERFALL */}
            <WealthWaterfall r={r} inp={inp} />

            {/* DONUTS */}
            <MoneyDonuts r={r} inp={inp} />

            {/* Secondary KPI strip — the supporting numbers */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/[0.06]" data-testid="kpi-strip">
              <KPI label="Out-of-pocket" value={fmtEUR(r.investment)} sub="Equity + acquisition costs" />
              <KPI label="Price per m²" value={fmtEUR(inp.kpApt / inp.sqm)} />
              <KPI label="Gross yield" value={fmtPct(r.grossYield * 100)} sub={`${fmtEUR(inp.rent)}/mo cold rent`} />
              <KPI label="Property value Y10" value={fmtEUR(r.valueEnd)} sub={`+${fmtPct(inp.valueGrowth * 100, 1)} p.a.`} />
              <KPI label="Remaining debt Y10" value={fmtEUR(r.finalBalance)} tone="neg" sub="After amortization" />
            </div>

            {/* Construction phase compact */}
            {r.constr && (
              <div className="bg-[#0a0a0a] border border-white/[0.06] p-6 md:p-7">
                <div className="mb-5">
                  <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">Before move-in</div>
                  <h3 className="font-heading text-xl md:text-2xl text-white">Construction phase · {r.constr.months} months remaining</h3>
                  <div className="font-body text-sm text-white/70 mt-1.5">During build, only the drawn part of your loan accrues interest. The tax office refunds your share via Anlage V.</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06]">
                  <KPI label="Avg drawn" value={fmtEUR(r.constr.avgDrawn)} sub={`${fmtPct(inp.constrDrawPct * 100, 0)} of loans`} />
                  <KPI label="Total interest" value={`−${fmtEUR(r.constr.totalInterest)}`} sub={`≈ ${fmtEUR(r.constr.monthlyAvgInterest)}/mo gross`} tone="neg" />
                  <KPI label="Tax cashback" value={`+${fmtEUR(r.constr.taxSaving)}`} sub={`${fmtPct(inp.taxRate * 100, 0)} marginal rate`} tone="pos" />
                  <KPI label="Net cost" value={fmtEUR(r.constr.netCost)} sub={`≈ ${fmtEUR(r.constr.monthlyAvgNet)}/mo net`} tone="neg" />
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* CONTENT — DETAILED VIEW */}
        {view === "detailed" && (
          <motion.div
            key="detailed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-7"
          >
            <DetailedMatrix r={r} inp={inp} />
          </motion.div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-br from-ire-accent/10 to-transparent border border-ire-accent/30 p-8 md:p-10 text-center mt-10">
          <h3 className="font-heading text-2xl md:text-3xl text-white mb-3">Like the numbers? Get the strategy.</h3>
          <p className="font-body text-sm text-white/80 max-w-xl mx-auto mb-7">
            The model shows what's possible. A 45-minute strategy call shows what's right for <em>your</em> income,
            tax bracket, and capital base — and which Off Market Karlshorst unit fits.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={openCalendly}
              data-testid="projects-cta-call"
              className="font-body text-xs tracking-[0.15em] uppercase text-black bg-ire-accent px-7 py-3.5 hover:bg-ire-accentHover transition-all flex items-center gap-2 group"
            >
              Book strategy call <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate("/apply")}
              data-testid="projects-cta-apply"
              className="font-body text-xs tracking-[0.15em] uppercase text-ire-accent border border-ire-accent/30 px-7 py-3.5 hover:bg-ire-accent/10 transition-all"
            >
              Apply for advisory
            </button>
          </div>
        </div>
      </div>

      {/* Floating "Adjust inputs" pill */}
      <FloatingAdjustButton onClick={scrollToInputs} testId="floating-adjust-inputs" sectionRef={sectionRef} />
    </section>
  );
};

// HERO — Apple-style parallax (desktop only — parallax disabled on mobile
// to prevent compositor thrash and phone-hang reports from clients).
const Hero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY            = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? ["0%", "0%"]   : ["0%", "30%"]);
  const bgScale        = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? [1, 1]          : [1.05, 1.15]);
  const contentY       = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? ["0%", "0%"]   : ["0%", "-12%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? [1, 1]          : [1, 0.4]);
  return (
    <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden">
      {/* Background image — parallax slow-drift */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale: bgScale }}>
        <Img
          src="/assets/karls/hero-architecture.jpg"
          alt="Modern KfW residential architecture — wide editorial frame"
          sizes="100vw"
          loading="eager"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-65"
        />
        {/* Vertical fade so headline area sits on cinematic dark */}
        <div className="absolute inset-0 bg-gradient-to-b from-ire-black/55 via-ire-black/60 to-ire-black" />
        {/* Left vignette for headline contrast */}
        <div className="absolute inset-0 bg-gradient-to-r from-ire-black/90 via-ire-black/40 to-transparent" />
        {/* Subtle warm gold tint to bind to the gold accent system */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-ire-accent/[0.05] mix-blend-overlay" />
      </motion.div>

      {/* Decorative glows — also fade as you scroll */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-ire-accent/[0.08] rounded-full blur-[180px]"
      />

      <motion.div style={{ y: contentY }} className="relative z-10 max-w-[1800px] mx-auto px-4 sm:px-6 md:px-16 lg:px-24 py-24 sm:py-32 md:py-40 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">Flagship Project · Berlin-Karlshorst</span>
          </div>
          <h1 className="font-heading text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-[1] sm:leading-[0.95] mb-6 sm:mb-8">
            Off Market Karlshorst<br />
            <span className="text-ire-accent italic font-light">A future-proof investment in Berlin's #2 growth district.</span>
          </h1>
          <p className="font-body text-base sm:text-lg lg:text-xl text-white/65 max-w-2xl leading-relaxed mb-10">
            100 KfW 40 NH apartments. QNG Plus certified. 5% degressive AfA + §7b Sonder-AfA.
            Completion Q4 2028. This is the editorial investment case — the macro thesis, the asset, and the math.
          </p>
          <div className="flex flex-wrap items-center gap-8 text-xs">
            <div>
              <div className="font-heading text-3xl text-ire-accent tabular-nums">100</div>
              <div className="font-body text-[10px] tracking-[0.25em] text-white/40 uppercase mt-1">Units</div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div>
              <div className="font-heading text-3xl text-ire-accent tabular-nums">5,800 m²</div>
              <div className="font-body text-[10px] tracking-[0.25em] text-white/40 uppercase mt-1">Living area</div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div>
              <div className="font-heading text-3xl text-ire-accent tabular-nums">Q4 2028</div>
              <div className="font-body text-[10px] tracking-[0.25em] text-white/40 uppercase mt-1">Completion</div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div>
              <div className="font-heading text-3xl text-ire-accent tabular-nums">KfW 40 NH</div>
              <div className="font-body text-[10px] tracking-[0.25em] text-white/40 uppercase mt-1">Energy class</div>
            </div>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-12 left-6 sm:left-8 md:left-16 lg:left-24 flex items-center gap-3"
        >
          <span className="font-body text-[10px] tracking-[0.4em] text-white/40 uppercase">Read the case</span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="h-8 w-px bg-ire-accent/40"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// CLOSING CTA
// ═════════════════════════════════════════════════════════
const ClosingCTA = () => {
  const navigate = useNavigate();
  const steps = [
    {
      n: "01",
      title: "Strategy call",
      body: "30 minutes, no obligation. We understand your investment goals, tax situation and financing structure.",
    },
    {
      n: "02",
      title: "Unit selection",
      body: "We send three pre-shortlisted units matching your profile. Reservation is non-binding and free.",
    },
    {
      n: "03",
      title: "Notary appointment",
      body: "We coordinate developer, bank and notary. Closing typically 4–8 weeks from reservation.",
    },
  ];

  return (
    <section className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#080808] to-ire-black" />
      <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-ire-accent/[0.04] to-transparent" />
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-ire-accent/[0.05] rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-ire-accent/[0.04] rounded-full blur-[180px]" />

      <div className="relative z-10 max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">The next step</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-8 max-w-4xl">
            Ready to move on<br />
            <span className="text-ire-accent italic">Off Market Karlshorst?</span>
          </h2>
          <p className="font-body text-base sm:text-lg text-white/65 max-w-3xl leading-relaxed mb-16">
            A significant share of the 100 units is already reserved. Book a 30-minute strategy call —
            we confirm your budget, shortlist three units matching your profile, and manage everything through to notary.
          </p>
        </Reveal>

        {/* Process steps */}
        <Reveal delay={0.1}>
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.06] mb-16">
            {steps.map((s) => (
              <div key={s.n} className="bg-[#050505] p-8 md:p-10">
                <div className="flex items-baseline justify-between mb-6">
                  <span className="font-mono text-3xl text-ire-accent tabular-nums">{s.n}</span>
                  <div className="h-px w-12 bg-ire-accent/30" />
                </div>
                <h3 className="font-heading text-xl md:text-2xl text-white mb-4">{s.title}</h3>
                <p className="font-body text-sm text-white/55 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Primary + secondary CTAs */}
        <Reveal delay={0.2}>
          <div className="flex flex-col items-center gap-6 text-center">
            <button
              onClick={openCalendly}
              data-testid="projects-closing-cta-call"
              className="font-body text-xs tracking-[0.2em] uppercase text-black bg-ire-accent px-10 py-4 hover:bg-ire-accentHover transition-all flex items-center gap-3 group"
            >
              Book a strategy call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="flex flex-wrap items-center gap-4 text-xs text-white/50">
              <a
                href="mailto:info@ireberlin.com"
                className="hover:text-ire-accent transition-colors"
                data-testid="projects-closing-cta-email"
              >
                info@ireberlin.com
              </a>
              <span className="text-white/20">·</span>
              <a
                href="tel:+491621871064"
                className="hover:text-ire-accent transition-colors"
                data-testid="projects-closing-cta-phone"
              >
                +49 162 1871064
              </a>
            </div>
            <p className="font-body text-[11px] text-white/30 leading-relaxed max-w-2xl mt-6">
              This page is a marketing summary prepared by IRE Berlin. It does not constitute investment, legal or tax advice.
              Pricing valid as per developer price list May 2026, subject to availability.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════
const ProjectsPage = () => (
  <DealroomLockComponent projectName="Off Market Karlshorst · Berlin-Karlshorst">
    <ProjectsPageContent />
  </DealroomLockComponent>
);

const ProjectsPageContent = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Apple-style buttery-smooth scroll (Lenis) — DESKTOP ONLY.
  // On mobile, Lenis fights native iOS/Android momentum scroll and causes
  // the page to "hang" / stutter on low-end devices. Mobile uses the
  // browser's native momentum scrolling which is already GPU-accelerated.
  useEffect(() => {
    const isMobile = matchMedia('(hover: none) and (pointer: coarse)').matches
      || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    if (isMobile) return;

    const lenis = new Lenis({
      duration: 1.6,
      // Apple-style ease-out: long luxurious tail
      easing: (t) => 1 - Math.pow(1 - t, 4),
      smoothWheel: true,
      wheelMultiplier: 0.85,
      touchMultiplier: 1.6,
      lerp: 0.08,
      // Let Lenis manage its own html.lenis / lenis-smooth classes
    });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="relative bg-ire-black text-white" data-testid="projects-page">
      <HeroPreload src="/assets/karls/hero-architecture.jpg" sizes="100vw" />
      <ScrollProgressHUD />
      <SectionAnchorNav />
      <AmbientCursorGlow />
      <Hero />
      <section id="section-macro"><MacroCase /></section>
      <section id="section-asset"><AssetDetails /></section>
      <section id="section-faq"><FaqObjections /></section>
      <ClosingCTA />
      <DealroomShareWidget project="karls-living-ii" projectLabel="Off Market Karlshorst · Berlin-Karlshorst" />
    </div>
  );
};

export {
  CalculatorAct,
  Reveal,
  ScrollProgressHUD,
  SectionAnchorNav,
  AmbientCursorGlow,
  FaqObjections,
  ClosingCTA,
};
export default ProjectsPage;
